'use client'

import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useChat } from '@ai-sdk/react'
import { MiniTopic, ChatUIMessage } from '@deepguide-ai/types'
import { UIMessage } from 'ai'
import { selectRequestMessages } from './lib/request-messages'
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  MessageSquareMore,
  PlusIcon,
  XIcon,
  ArrowRight,
  Camera,
  FilePlus2,
  FileText,
  Loader2,
  Sparkles,
} from 'lucide-react'
import { nanoid } from 'nanoid'
import { ErrorBoundary } from 'react-error-boundary'
import { toast } from 'sonner'
import { useStickToBottomContext } from 'use-stick-to-bottom'
import {
  chatProgrammaticMessageAtom,
  artifactPanelStateAtom,
  pendingOptionsPromptAtom,
  screenataPageContextAtom,
  type PendingOptionsPrompt,
  type ScreenataPageContext,
} from '@/stores/screenataPageContextStore'
import { screenataSidebarChatViewAtom } from '@/stores/screenataPageContextStore'
import {
  ChatInputFormCard,
  type ChatFormSpec,
} from '@/components/chat/components/messages/ChatInputFormCard'

/** A pending `presentForm` deferred tool awaiting the user's submitted values. */
interface PendingFormPrompt {
  toolCallId: string
  spec: ChatFormSpec
}
import { apiEndpoints } from '@/lib/env'
import {
  getWorkspaceIdFromPath,
  isWorkspaceChatPath,
} from '@/lib/screenata-phase'
import {
  CHROME_WEB_STORE_URL,
  startBrowserCapture,
  startBrowserRemedy,
} from '@/lib/extensionUtils'
import { useExtensionInstalled } from '@/hooks/useExtensionInstalled'
import {
  CaptureFlowContext,
  formatCaptureDestinationLabel,
  type CaptureFlowContextValue,
  type PresentCaptureFlowPayload,
  type PresentRemedyFlowPayload,
} from './capture-flow-context'
import { trpcClient } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  activeOrganizationWithStorageAtom,
  activeWorkspaceWithStorageAtom,
} from '@/stores/workspaceStore'
import { useTopicAtomsContext } from '@/hooks/useTopicAtomsContext'
import { Button } from '@deepguide-ai/ui/button'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
// import { Badge } from '@deepguide-ai/ui/badge'
import {
  Conversation,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation'
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuItem,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input'
import { KbTooltip } from '@deepguide-ai/ui/kb-tooltip'
import { ChatThread, ChatThreadManager } from './ChatThreadManager'
import ChatHeader from './chat-header'
import {
  AttachmentPill,
  UploadedAttachmentPill,
  UploadingAttachmentPill,
} from './components/AttachmentPill'
import {
  AttachDocumentDialog,
  type AttachedDocItem,
} from './components/AttachDocumentDialog'
import { ChatThreadList } from './components/ChatThreadList'
import { ChatWelcomeBody, ChatWelcomeHeader } from './components/ChatWelcome'
import { EditorContextBadge } from './components/EditorContextBadge'
import ErrorStatus from './components/ErrorStatus'
import { Messages, PAGE_SUGGESTIONS } from './messages'
import {
  assignThreadWorkspaceAtom,
  createThreadAtom,
  currentThreadIdAtom,
  loadThreadsAtom,
  openThreadHistoryAtom,
  syncThreadMessagesAtom,
  updateThreadNameAtom,
} from './store/chatThreadStore'

type ChatProps = {
  messages: UIMessage[]
  topic?: MiniTopic
  docId?: string
  onClose?: () => void
  status?: 'streaming' | 'awaiting_message' | 'in_progress' | 'error' | 'ready'
  generationStatus?: string
  isGenerationMode?: boolean
  generationError?: { message?: string } | null
  api?: string
  requestBody?: Record<string, unknown>
  /** Called at send-time to snapshot the current page content as markdown */
  getPageContent?: () => string | undefined
  sendOnlyLastMessage?: boolean
  mode?: 'sidebar' | 'floating'
  onToggleMode?: () => void
  onToggleExpand?: () => void
  isExpanded?: boolean
  onMessagesChange?: (messages: UIMessage[]) => void
  /** Called when the AI stream finishes (server has completed all post-processing) */
  onStreamFinish?: (message: UIMessage) => void
  hideHeader?: boolean
  hideCloseButton?: boolean
  /**
   * Force the full chat-home welcome layout. This is URL-driven by the
   * workspace shell and intentionally bypasses stale pageContext from the
   * previous route during the first navigation frame.
   */
  forceWelcomeLayout?: boolean
  /**
   * Auto-start trigger. Plain string = send that text. Object form lets
   * callers attach a per-call `body` override that's merged into the
   * request for THIS send only.
   */
  autoStartMessage?:
    | string
    | { message: string; body?: Record<string, unknown> }
  /** Persistent content rendered above the PromptInput (e.g., a "Continue"
   * CTA pill). Takes precedence over the sidecar click-to-continue wedge
   * when both would otherwise render. */
  inputBanner?: React.ReactNode
}

const SHOW_CHAT_CAPTURE_SHORTCUT = false
const CHAT_UPLOAD_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024
const CHAT_UPLOAD_MAX_FILE_SIZE_LABEL = '25 MB'
function readToolPayload(part: any) {
  try {
    if (part?.output != null) {
      return typeof part.output === 'string' ? JSON.parse(part.output) : part.output
    }
    if (part?.toolInvocation?.result) {
      return typeof part.toolInvocation.result === 'string'
        ? JSON.parse(part.toolInvocation.result)
        : part.toolInvocation.result
    }
    if (part?.input) {
      return typeof part.input === 'string' ? JSON.parse(part.input) : part.input
    }
    if (part?.toolInvocation?.args) return part.toolInvocation.args
  } catch {
    return null
  }
  return null
}

function ChatScrollToBottomBridge({ scope }: { scope: string | null }) {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext()

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { scope?: string | null; onlyIfAtBottom?: boolean }
        | undefined
      if (scope && detail?.scope && detail.scope !== scope) return
      if (detail?.onlyIfAtBottom && !isAtBottom) return

      requestAnimationFrame(() => scrollToBottom())
      window.setTimeout(() => scrollToBottom(), 50)
    }

    window.addEventListener('screenata:chat-scroll-bottom', handler)
    return () =>
      window.removeEventListener('screenata:chat-scroll-bottom', handler)
  }, [isAtBottom, scope, scrollToBottom])

  return null
}

function wrapActivityContext(data: Record<string, unknown>) {
  return {
    turnContext: { pageContext: { page: 'unknown', summary: '', data } },
  }
}

// Page → skills to auto-load so the skill's workflow guidance is in context
// from turn 0. The evidence/check tools are always-on, so without this the
// orchestrator can do evidence work WITHOUT ever loading the skill — and miss
// its routing (e.g. "present Connect AWS when a fitting check's integration
// isn't set up", from server/skills/evidence + tests SKILL.md). Re-sending it
// each turn is idempotent server-side (deterministic loadSkill toolCallId).
// Page → skill(s) to auto-load so the skill's workflow guidance + tools are in
// context from turn 0. Domain tools are skill-gated (always-on is just
// loadSkill/getPageContent/presentOptions), so on a page whose chat is
// overwhelmingly about one domain, preloading it saves a wasted loadSkill
// round-trip and guarantees the guidance is present. The orchestrator still
// loads other skills for off-domain asks; idle skills auto-evict. Each skill
// listed here must be self-contained (own its tools + guidance). Skipped:
// integration-scan / runbook-list (ambiguous — multiple plausible skills).
const PAGE_AUTOLOAD_SKILLS: Record<string, string[]> = {
  // entity-detail pages — the chat is almost always about this one thing
  'test-detail': ['evidence'],
  'asset-detail': ['assets'],
  'document-detail': ['documents'],
  'policy-detail': ['policies'],
  'runbook-detail': ['runbook-execution'],
  // control detail uses `controlDetail` (camelCase) — the controls skill covers
  // both list and detail (controlsList + controlsGet); there's no separate one.
  controlDetail: ['controls'],
  // single-domain list pages
  risks: ['risks'],
  assets: ['assets'],
  documents: ['documents'],
  vendors: ['vendors'],
  policies: ['policies'],
  evidence: ['evidence'],
  people: ['population'],
  readiness: ['program'],
  // /tests route shows both the Controls coverage view and the Tests list
  // (toggled by ?view=) — load both skills regardless of the active view.
  tests: ['tests', 'controls'],
}

function formatPresentOptionsMessage(
  status: 'answered' | 'dismissed',
  selected: string | string[] | null | undefined
) {
  if (status === 'dismissed') {
    return 'User dismissed the options without picking one.'
  }
  const text = Array.isArray(selected) ? selected.join(', ') : selected ?? ''
  return `User picked: ${text}. Treat this as confirmation and execute. Do not re-ask.`
}

function PageContextDebugBar({
  pageContext,
  threadId,
  lastSentContext,
}: {
  pageContext: ScreenataPageContext | null
  threadId: string | null
  lastSentContext: { turnContext?: unknown; messageMetadata?: unknown } | null
}) {
  const [open, setOpen] = useState(false)
  const tc = lastSentContext?.turnContext as Record<string, unknown> | undefined
  const tcData = (tc?.pageContext as Record<string, unknown>)?.data as
    | Record<string, unknown>
    | undefined
  const msgMeta = lastSentContext?.messageMetadata as
    | Record<string, unknown>
    | undefined
  const action = (msgMeta?.action ?? tcData?.action) as string | undefined
  return (
    <div className='border-b border-dashed border-status-orange/30 bg-status-orange-bg font-mono text-[10px] text-status-orange-strong'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='flex w-full items-center gap-1.5 px-2 py-0.5 hover:bg-status-orange-subtle'
      >
        <span className='font-semibold'>ctx</span>
        <span className='truncate opacity-70'>
          {pageContext ? `${pageContext.page}` : '(none)'}
          {action ? ` [${action}]` : ''}
          {msgMeta ? ' +meta' : ''}
          {threadId ? ` · ${threadId.slice(0, 12)}…` : ''}
        </span>
        <span className='ml-auto'>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <pre className='max-h-[300px] overflow-auto px-2 pb-1 break-all whitespace-pre-wrap'>
          <span className='font-semibold text-status-orange'>atom:</span>
          {'\n'}
          {JSON.stringify({ pageContext, threadId }, null, 2)}
          {lastSentContext?.turnContext && (
            <>
              {'\n\n'}
              <span className='font-semibold text-status-blue'>
                last sent turnContext:
              </span>
              {'\n'}
              {JSON.stringify(lastSentContext.turnContext, null, 2)}
            </>
          )}
          {msgMeta && (
            <>
              {'\n\n'}
              <span className='font-semibold text-status-green-strong'>
                message metadata:
              </span>
              {'\n'}
              {JSON.stringify(msgMeta, null, 2)}
            </>
          )}
        </pre>
      )}
    </div>
  )
}

export function Chat(props: ChatProps) {
  const {
    status: initialStatus = 'ready',
    generationStatus = 'completed',
    isGenerationMode = false,
    messages: _initialMessages,
    topic,
    generationError = null,
    docId,
    onClose,
    api = apiEndpoints.chat,
    requestBody,
    getPageContent,
    sendOnlyLastMessage = true,
    mode,
    onToggleMode,
    onToggleExpand,
    isExpanded,
  } = props

  const chatEventScope = useMemo(() => {
    if (api.includes('/onboarding-chat')) return 'screenata-onboarding-chat'
    if (
      api.includes('/screenata-chat') &&
      typeof requestBody?.policySlug === 'string' &&
      requestBody.policySlug.trim().length > 0
    ) {
      return 'screenata-policy-chat'
    }
    if (api.includes('/screenata-chat')) return 'screenata-chat'
    return null
  }, [api, requestBody])

  const isOnboardingChat = useMemo(
    () => api.includes('/onboarding-chat'),
    [api]
  )
  const pageContext = useAtomValue(screenataPageContextAtom)
  const activeOrganization = useAtomValue(activeOrganizationWithStorageAtom)
  const activeWorkspace = useAtomValue(activeWorkspaceWithStorageAtom)
  const navigate = useNavigate()
  const routeLocation = useLocation()
  const routedThreadId = useMemo(() => {
    const match = routeLocation.pathname.match(
      /\/(?:screenata\/)?chat\/([^/?#]+)$/
    )
    return match ? match[1] : null
  }, [routeLocation.pathname])
  const currentWorkspaceId = useMemo(() => {
    const pathWorkspaceKey = getWorkspaceIdFromPath(routeLocation.pathname)
    const matchedWorkspace = pathWorkspaceKey
      ? activeOrganization?.workspaces?.find(
          (workspace) =>
            workspace.id === pathWorkspaceKey ||
            workspace.slug === pathWorkspaceKey
        )
      : undefined

    if (
      activeWorkspace?.id &&
      (!pathWorkspaceKey ||
        activeWorkspace.id === pathWorkspaceKey ||
        activeWorkspace.slug === pathWorkspaceKey)
    ) {
      return activeWorkspace.id
    }

    return (
      matchedWorkspace?.id ??
      pathWorkspaceKey ??
      (typeof window !== 'undefined'
        ? localStorage.getItem('auth:activeWorkspaceId')
        : null)
    )
  }, [
    activeOrganization?.workspaces,
    activeWorkspace?.id,
    activeWorkspace?.slug,
    routeLocation.pathname,
  ])
  const isScreenataChatViewActive = useAtomValue(screenataSidebarChatViewAtom)

  const createThread = useSetAtom(createThreadAtom)
  const assignThreadWorkspace = useSetAtom(assignThreadWorkspaceAtom)
  const [openThreadHistory, setOpenThreadHistory] = useAtom(
    openThreadHistoryAtom
  )

  const { editorRef } = useTopicAtomsContext()
  const [showThreadList, setShowThreadList] = useState(false)
  const [lastSentContext, setLastSentContext] = useState<Record<
    string,
    unknown
  > | null>(null)

  useEffect(() => {
    const handler = () => setShowThreadList(true)
    window.addEventListener('screenata:open-history', handler)
    return () => window.removeEventListener('screenata:open-history', handler)
  }, [])

  useEffect(() => {
    if (!openThreadHistory) return
    setShowThreadList(true)
    setOpenThreadHistory(false)
  }, [openThreadHistory, setOpenThreadHistory])
  const [currentThreadId, setCurrentThreadId] = useAtom(currentThreadIdAtom)
  const [chatError, setChatError] = useState<string | null>(null)
  // Ref into `chat.setMessages`, populated AFTER `useChat` returns. We
  // need it accessible from inside the `onError` callback that's passed
  // INTO `useChat`, where `setMessages` doesn't exist yet — and we don't
  // want to tear down the chat instance just to add inline error
  // recovery. `setMessages` itself is a stable function from the SDK.
  const setMessagesRef = useRef<
    ((updater: (current: ChatUIMessage[]) => ChatUIMessage[]) => void) | null
  >(null)
  const syncThreadMessages = useSetAtom(syncThreadMessagesAtom)
  const updateThreadName = useSetAtom(updateThreadNameAtom)
  const loadThreads = useSetAtom(loadThreadsAtom)
  const setPendingOptionsPrompt = useSetAtom(pendingOptionsPromptAtom)

  useEffect(() => {
    if (!currentThreadId || !currentWorkspaceId) return
    if (routedThreadId !== currentThreadId) return
    void assignThreadWorkspace({
      threadId: currentThreadId,
      workspaceId: currentWorkspaceId,
    })
  }, [
    assignThreadWorkspace,
    currentThreadId,
    currentWorkspaceId,
    routedThreadId,
  ])

  // RFC-068: File upload state
  const [uploadingFiles, setUploadingFiles] = useState<
    Map<string, { name: string; progress: number }>
  >(new Map())
  const [uploadedSources, setUploadedSources] = useState<
    Array<{ id: string; name: string; type?: string; url?: string }>
  >([])
  const [input, setInput] = useState('')
  const [extensionInstalled, setExtensionInstalled] = useExtensionInstalled()
  const [captureStartingId, setCaptureStartingId] = useState<string | null>(
    null
  )
  const [remedyStartingId, setRemedyStartingId] = useState<string | null>(
    null
  )

  // RFC-069: Multi-document context state
  const [allDocContexts, setAllDocContexts] = useState<
    Array<{
      docId: string
      topicId?: string
      topicTitle?: string
      selection: { from: number; to: number }
      selectedText: string
      timestamp: Date
      editorContent?: string
    }>
  >([])

  // RFC-070: Store sources by message ID for citation hover
  const [messageSources, setMessageSources] = useState<Map<string, any[]>>(
    new Map()
  )

  // 控制“+”动作菜单开关，便于在选择文件后自动关闭
  const [actionMenuOpen, setActionMenuOpen] = useState(false)

  // Existing vault docs / policies attached from the "+" menu. Unlike file
  // uploads, these reference live PolicyDocument/VaultDocument rows; the
  // agent resolves their content server-side (no file copy).
  const [attachedDocs, setAttachedDocs] = useState<AttachedDocItem[]>([])
  const [attachDocDialogOpen, setAttachDocDialogOpen] = useState(false)

  const captureContext = useMemo(() => {
    const data = (pageContext?.data ?? {}) as Record<string, unknown>
    const pathWorkspaceId =
      typeof window !== 'undefined'
        ? getWorkspaceIdFromPath(window.location.pathname)
        : null
    const workspaceId =
      (typeof data.workspaceId === 'string' ? data.workspaceId : null) ??
      pathWorkspaceId ??
      (typeof window !== 'undefined'
        ? localStorage.getItem('auth:activeWorkspaceId')
        : null) ??
      ''
    const testId =
      (typeof data.testId === 'string' ? data.testId : null) ??
      (typeof data.cesId === 'string' ? data.cesId : null) ??
      (typeof data.statusId === 'string' ? data.statusId : null) ??
      (typeof data.controlEvidenceStatusId === 'string'
        ? data.controlEvidenceStatusId
        : null)
    const controlDefId =
      (typeof data.controlDefId === 'string' ? data.controlDefId : null) ??
      (typeof data.controlDefinitionId === 'string'
        ? data.controlDefinitionId
        : null)
    const rawTestTitle =
      (typeof data.testTitle === 'string' ? data.testTitle : null) ??
      (typeof data.title === 'string' ? data.title : null)
    const taskCode =
      typeof data.taskCode === 'string' && data.taskCode.trim().length > 0
        ? data.taskCode.trim()
        : null
    const controlRefs =
      typeof data.controlRefs === 'string' ? data.controlRefs : null
    return {
      workspaceId,
      testId,
      controlDefId,
      testTitle: rawTestTitle,
      taskCode,
      controlRefs,
    }
  }, [pageContext])

  const handleStartBrowserCapture = useCallback(
    async (payload?: PresentCaptureFlowPayload, toolCallId?: string) => {
      const startId = toolCallId ?? 'composer'
      // Never short-circuit on the cached `extensionInstalled` flag: the real
      // handshake below is the source of truth, and it opens the store itself
      // on `no-ack`. Trusting a stale negative is what made this button always
      // send installed users to the Chrome Web Store.
      if (captureStartingId) return

      const workspaceId = captureContext.workspaceId
      const taskId = payload?.testId ?? captureContext.testId ?? undefined
      const controlDefIds = captureContext.controlDefId
        ? [captureContext.controlDefId]
        : undefined

      if (!workspaceId || (!taskId && !controlDefIds?.length)) {
        toast.error('Open a test or control page before starting capture.')
        return
      }

      setCaptureStartingId(startId)
      try {
        const isCurrentTest = payload?.testId === captureContext.testId
        const currentTestLabel =
          captureContext.taskCode && captureContext.testTitle
            ? `${captureContext.taskCode}: ${captureContext.testTitle}`
            : (captureContext.testTitle ?? captureContext.taskCode)
        const contextLabel = payload
          ? isCurrentTest && currentTestLabel
            ? currentTestLabel
            : formatCaptureDestinationLabel({
                flowTestId: payload.testId,
                contextTestId: captureContext.testId,
                taskCode: captureContext.taskCode,
                testTitle: captureContext.testTitle,
                controlRefs: captureContext.controlRefs,
              })
          : 'Chat capture'
        const result = await startBrowserCapture({
          organizationId: activeOrganization?.id,
          workspaceId,
          taskId,
          controlDefIds,
          targetUrl: payload?.entryUrl,
          source: payload ? 'chat-agent-flow' : 'chat',
          contextLabel,
        })

        if (result.started) {
          toast.success(
            result.sidepanelOpened
              ? 'Capture opened in the browser side panel.'
              : (result.fallbackMessage ??
                  'Capture queued. Open the extension side panel to continue.')
          )
          setExtensionInstalled(true)
          if (toolCallId) setDismissedPromptId(toolCallId)
          return
        }

        if (result.reason === 'no-ack') {
          setExtensionInstalled(false)
          window.open(CHROME_WEB_STORE_URL, '_blank')
          toast.message('Install Screenata Capture to use browser capture.')
        } else {
          toast.error(
            result.fallbackMessage ??
              result.error ??
              'Could not start browser capture.'
          )
        }
      } finally {
        setCaptureStartingId(null)
      }
    },
    [
      activeOrganization?.id,
      captureContext.controlDefId,
      captureContext.testId,
      captureContext.workspaceId,
      captureStartingId,
      setExtensionInstalled,
    ]
  )

  const handleStartBrowserRemedy = useCallback(
    async (payload: PresentRemedyFlowPayload, toolCallId: string) => {
      // Same as capture: let the handshake decide, not the cached flag.
      if (captureStartingId || remedyStartingId) return

      const workspaceId = captureContext.workspaceId
      const taskId = payload.testId ?? captureContext.testId ?? undefined

      if (!workspaceId || (!taskId && !payload.remediationTicketId)) {
        toast.error('Open a finding or test before starting browser remedy.')
        return
      }

      setRemedyStartingId(toolCallId)
      try {
        const isCurrentTest = payload.testId === captureContext.testId
        const currentTestLabel =
          captureContext.taskCode && captureContext.testTitle
            ? `${captureContext.taskCode}: ${captureContext.testTitle}`
            : (captureContext.testTitle ?? captureContext.taskCode)
        const contextLabel =
          isCurrentTest && currentTestLabel
            ? currentTestLabel
            : (payload.title ?? 'Browser remedy')

        const result = await startBrowserRemedy({
          organizationId: activeOrganization?.id,
          workspaceId,
          taskId,
          remediationTicketId: payload.remediationTicketId,
          targetUrl: payload.entryUrl,
          source: 'chat-agent-remedy-flow',
          contextLabel,
          agentGuidance: {
            rationale: payload.rationale,
            entryUrl: payload.entryUrl,
            frames: payload.steps,
          },
        })

        if (result.started) {
          toast.success(
            result.sidepanelOpened
              ? 'Remedy opened in the browser side panel.'
              : (result.fallbackMessage ??
                  'Remedy queued. Open the extension side panel to continue.')
          )
          setExtensionInstalled(true)
          setDismissedPromptId(toolCallId)
          return
        }

        if (result.reason === 'no-ack') {
          setExtensionInstalled(false)
          window.open(CHROME_WEB_STORE_URL, '_blank')
          toast.message('Install Screenata Capture to use browser remedy.')
        } else {
          toast.error(
            result.fallbackMessage ??
              result.error ??
              'Could not start browser remedy.'
          )
        }
      } finally {
        setRemedyStartingId(null)
      }
    },
    [
      activeOrganization?.id,
      captureContext.taskCode,
      captureContext.testId,
      captureContext.testTitle,
      captureContext.workspaceId,
      captureStartingId,
      remedyStartingId,
      setExtensionInstalled,
    ]
  )

  const captureFlowContextValue = useMemo<CaptureFlowContextValue>(
    () => ({
      extensionInstalled,
      captureStartingId,
      remedyStartingId,
      captureContext,
      startCapture: (payload, toolCallId) =>
        handleStartBrowserCapture(payload, toolCallId),
      startRemedy: (payload, toolCallId) =>
        handleStartBrowserRemedy(payload, toolCallId),
    }),
    [
      extensionInstalled,
      captureStartingId,
      remedyStartingId,
      captureContext,
      handleStartBrowserCapture,
      handleStartBrowserRemedy,
    ]
  )

  // Calculate selectedText early for use in callbacks
  const selectedText = useMemo(() => {
    if (!editorRef) return ''
    const { from, to } = editorRef.state.selection
    if (from === to) return ''
    const slice = editorRef.state.doc.slice(from, to)
    return editorRef.storage.markdown?.serializer.serialize(slice.content) || ''
  }, [editorRef])

  // RFC-068: Handle file upload when files are added (not on send)
  const handleFilesAdded = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return

      // 选择文件（打开文件对话框并确认）后，关闭动作菜单
      setActionMenuOpen(false)

      const acceptedFiles = files.filter(
        (file) => file.size <= CHAT_UPLOAD_MAX_FILE_SIZE_BYTES
      )
      const rejectedFiles = files.filter(
        (file) => file.size > CHAT_UPLOAD_MAX_FILE_SIZE_BYTES
      )

      if (rejectedFiles.length > 0) {
        toast.error(
          rejectedFiles.length === 1
            ? `${rejectedFiles[0].name} is larger than ${CHAT_UPLOAD_MAX_FILE_SIZE_LABEL}.`
            : `${rejectedFiles.length} files are larger than ${CHAT_UPLOAD_MAX_FILE_SIZE_LABEL}.`
        )
      }

      if (acceptedFiles.length === 0) return

      console.log(
        `[Chat] RFC-068: Uploading ${acceptedFiles.length} file(s) immediately...`
      )

      const newSources: Array<{
        id: string
        name: string
        url: string
        type: string
      }> = []
      const uploadMap = new Map<string, { name: string; progress: number }>()

      // Add all files to uploading state
      for (const file of acceptedFiles) {
        const fileId = nanoid()
        uploadMap.set(fileId, { name: file.name, progress: 0 })
      }
      setUploadingFiles(uploadMap)

      // Upload files in parallel
      try {
        const uploadPromises = Array.from(uploadMap.entries()).map(
          async ([fileId, fileInfo], index) => {
            const file = acceptedFiles[index]
            if (!file) return

            try {
              // Convert file to base64
              const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => {
                  const base64String = (reader.result as string).split(',')[1]
                  resolve(base64String)
                }
                reader.onerror = reject
                reader.readAsDataURL(file)
              })

              // Update progress - converting done
              uploadMap.set(fileId, { name: fileInfo.name, progress: 50 })
              setUploadingFiles(new Map(uploadMap))

              // Upload via tRPC
              const source = await trpcClient.sources.uploadFileContent.mutate({
                filename: file.name,
                contentType: file.type || 'application/octet-stream',
                content: base64,
                size: file.size,
              })
              newSources.push({
                id: source.id,
                name: file.name,
                url: source.file.url,
                type: source.file.type,
              })

              // Update progress - upload complete
              uploadMap.set(fileId, { name: fileInfo.name, progress: 100 })
              setUploadingFiles(new Map(uploadMap))

              console.log(
                `[Chat] RFC-068: Uploaded ${file.name} → sourceId: ${source.id}`
              )
            } catch (error) {
              console.error(
                `[Chat] RFC-068: Failed to upload ${file.name}:`,
                error
              )
              toast.error(`Failed to upload ${file.name}`)
              uploadMap.delete(fileId)
              setUploadingFiles(new Map(uploadMap))
            }
          }
        )

        await Promise.all(uploadPromises)

        // Clear uploading state
        setUploadingFiles(new Map())

        // Add to uploaded files
        setUploadedSources((prev) => [...prev, ...newSources])
      } catch (error) {
        console.error('[Chat] RFC-068: File upload failed:', error)
        toast.error('File upload failed')
        setUploadingFiles(new Map())
      }
    },
    []
  )

  const handleSubmit = async (message: PromptInputMessage) => {
    if (isStreamingResponse) {
      stop()
      return
    }

    const hasText = Boolean(message.text?.trim())
    const hasAttachments =
      Boolean(message.files?.length) ||
      uploadedSources.length > 0 ||
      attachedDocs.length > 0

    if (!(hasText || hasAttachments)) {
      return
    }

    // RFC-069: Auto-add current document to context if not already present
    if (
      docId &&
      currentThreadId &&
      !allDocContexts.find((ctx) => ctx.docId === docId)
    ) {
      const _wasAdded = await addOrUpdateDocContext()
    }

    // RFC-067: Pass editor context to enable source-grounded chat
    const messageBody: any = {}

    // Add editor context if available (from AI selector or current editor state)
    if (currentThread?.editorContext) {
      const ctx = currentThread.editorContext
      messageBody.selection = ctx.selection
      messageBody.selectedText = ctx.selectedText
      messageBody.topicId = ctx.topicId

      console.log('[Chat] Using editor context from thread:', {
        topicId: ctx.topicId,
        selectionRange: ctx.selection,
        selectedTextLength: ctx.selectedText?.length || 0,
      })
    } else if (topic?.id) {
      // Fall back to current editor state
      messageBody.topicId = topic.id
      if (editorRef && editorRef.state && editorRef.state.selection) {
        const { from, to } = editorRef.state.selection
        messageBody.selection = { from, to }
        messageBody.selectedText = selectedText

        // Extract blockId from current selection to pass to agent for claim anchoring
        let blockId: string | undefined
        editorRef.state.doc.nodesBetween(from, from, (node) => {
          if (node.attrs?.id && !blockId) {
            blockId = node.attrs.id
          }
        })
        if (blockId) {
          messageBody.blockId = blockId
        }
      }
      console.log('[Chat] Using current editor state')
    }

    // Add editor content for source-grounded assistance
    if (editorRef) {
      const editorContent = editorRef.getJSON()
      messageBody.editorContent = JSON.stringify(editorContent)
      console.log('[Chat] Including editor content in message body')
    }

    // RFC-068: Include uploaded source IDs
    if (uploadedSources.length > 0) {
      messageBody.uploadedSourceIds = uploadedSources.map((s) => s.id)
      console.log(
        '[Chat] RFC-068: Including uploaded sources:',
        uploadedSources
      )
    }

    // Existing vault docs / policies attached from the "+" menu — agent
    // resolves their content server-side.
    if (attachedDocs.length > 0) {
      messageBody.attachedDocuments = attachedDocs.map((d) => ({
        documentType: d.documentType,
        documentId: d.documentId,
      }))
      console.log('[Chat] Including attached documents:', attachedDocs.length)
    }

    // Persist BOTH uploaded files and attached docs/policies onto the message
    // metadata so they re-render as pills on the sent bubble (and in history).
    // Files carry a preview url; docs carry the type/id/title needed for a pill.
    const attachmentMetadata =
      uploadedSources.length > 0 || attachedDocs.length > 0
        ? {
            ...(uploadedSources.length > 0 && {
              uiAttachments: uploadedSources.map(({ id, name, type, url }) => ({
                id,
                name,
                type,
                url,
              })),
            }),
            ...(attachedDocs.length > 0 && {
              uiDocumentAttachments: attachedDocs.map(
                ({ documentType, documentId, title }) => ({
                  documentType,
                  documentId,
                  title,
                })
              ),
            }),
          }
        : undefined

    // RFC-069: Include multi-document contexts
    if (allDocContexts.length > 0) {
      messageBody.documentContexts = allDocContexts
      messageBody.currentDocId = docId
      console.log(
        '[Chat] RFC-069: Including document contexts:',
        allDocContexts.length
      )
    } else if (editorRef && docId && topic?.id && messageBody.editorContent) {
      // Fallback: Auto-create document context from current editor state
      // This ensures ragSources are populated even without explicit documentContexts
      messageBody.documentContexts = [
        {
          docId: docId,
          topicId: topic.id,
          topicTitle: topic.title,
          selection: messageBody.selection || { from: 0, to: 0 },
          selectedText: messageBody.selectedText || '',
          timestamp: new Date(),
          editorContent: messageBody.editorContent,
        },
      ]
      messageBody.currentDocId = docId
      console.log(
        '[Chat] RFC-069: Auto-created document context from current editor state'
      )
    }

    console.log('[Chat] Sending message with body:', {
      hasSelection: !!messageBody.selection,
      hasSelectedText: !!messageBody.selectedText,
      hasEditorContent: !!messageBody.editorContent,
      uploadedSources: uploadedSources.length,
      documentContexts: allDocContexts.length,
      topicId: messageBody.topicId,
      messageText: message.text,
    })

    sendMessage(
      {
        text: message.text || 'Sent with attachments',
        files: message.files,
        ...(attachmentMetadata ? { metadata: attachmentMetadata } : {}),
      },
      {
        body: messageBody,
      }
    )
    window.dispatchEvent(
      new CustomEvent('screenata:chat-scroll-bottom', {
        detail: { scope: chatEventScope, onlyIfAtBottom: true },
      })
    )

    // Clear uploaded files after sending
    setUploadedSources([])
    setAttachedDocs([])
    setInput('')
  }

  // Get current thread for editor context display (RFC-067)
  const [currentThread, setCurrentThread] = useState<ChatThread | null>(null)

  // Load current thread when thread ID changes
  useEffect(() => {
    if (currentThreadId) {
      ChatThreadManager.getThread(currentThreadId).then((thread) => {
        setCurrentThread(thread)
      })
    } else {
      setCurrentThread(null)
    }
  }, [currentThreadId])

  // RFC-069: Load document contexts when thread changes
  useEffect(() => {
    const loadDocContexts = async () => {
      if (!currentThreadId) {
        setAllDocContexts([])
        return
      }

      const contexts =
        await ChatThreadManager.getDocumentContexts(currentThreadId)
      setAllDocContexts(contexts)
      console.log('[Chat] RFC-069: Loaded document contexts:', contexts.length)
    }

    loadDocContexts()
  }, [currentThreadId])

  // RFC-069: Helper function to add/update current document context
  const addOrUpdateDocContext = useCallback(
    async (force = false) => {
      if (!currentThreadId || !topic || !docId || !editorRef) return false

      // Get current selection (if any)
      const { from, to } = editorRef.state.selection
      const currentSelectedText = selectedText || ''

      // Check if current doc is already in contexts
      const existingContext = allDocContexts.find((ctx) => ctx.docId === docId)

      // If doc exists and no new selection, skip unless forced
      if (existingContext && !currentSelectedText && !force) {
        console.log(
          '[Chat] RFC-069: Document already in context, no new selection'
        )
        return false
      }

      // If doc exists with same selection, skip
      if (
        existingContext &&
        existingContext.selectedText === currentSelectedText &&
        !force
      ) {
        console.log('[Chat] RFC-069: Document context unchanged')
        return false
      }

      // Get full editor content
      const editorContent = editorRef.getText()

      // Create/update document context
      const newContext = {
        docId,
        topicId: topic.id,
        topicTitle: topic.title,
        selection: currentSelectedText ? { from, to } : { from: 0, to: 0 },
        selectedText: currentSelectedText || '(No selection)',
        timestamp: new Date(),
        editorContent,
      }

      // Add/update in thread (addDocumentContext handles both add and update)
      await ChatThreadManager.addDocumentContext(currentThreadId, newContext)

      // Update local state
      const updated =
        await ChatThreadManager.getDocumentContexts(currentThreadId)
      setAllDocContexts(updated)

      const action = existingContext ? 'Updated' : 'Added'
      console.log(`[Chat] RFC-069: ${action} document context:`, {
        docId,
        title: topic.title,
        selectionLength: currentSelectedText.length,
        totalDocs: updated.length,
      })

      return true
    },
    [currentThreadId, topic, docId, editorRef, selectedText, allDocContexts]
  )

  // RFC-069: Auto-update context when selection changes on existing doc
  useEffect(() => {
    const updateSelection = async () => {
      if (!currentThreadId || !docId || !selectedText) return

      const existingContext = allDocContexts.find((ctx) => ctx.docId === docId)
      if (existingContext && existingContext.selectedText !== selectedText) {
        // User made a new selection on an already-added document
        await addOrUpdateDocContext()
        console.log(
          '[Chat] RFC-069: Auto-updated selection for existing document'
        )
      }
    }

    updateSelection()
  }, [
    selectedText,
    docId,
    currentThreadId,
    allDocContexts,
    addOrUpdateDocContext,
  ])

  // Track when we're loading existing messages vs when messages are actually updated
  const isLoadingExistingMessages = useRef(false)
  // Keep the AI SDK store identity stable for the component lifetime. Draft
  // thread ids can rotate as the user starts new chats, but the useChat id
  // must not or AI SDK can briefly merge store subscribers with stale messages.
  const chatInstanceIdRef = useRef<string>(nanoid())
  const draftThreadIdRef = useRef<string>(nanoid())
  // Marks the exact `messages` reference that was just cleared during
  // navigation, so render-time welcome forcing and auto-create logic can
  // identify a stale thread snapshot without hiding real submitted messages.
  const staleMessagesRef = useRef<UIMessage[] | null>(null)

  // Stable ref for getPageContent so the transport doesn't recreate
  const getPageContentRef = useRef(getPageContent)
  getPageContentRef.current = getPageContent

  // Read screenataPageContext atom to merge into requests at send-time
  const screenataPageContext = useAtomValue(screenataPageContextAtom)
  const screenataPageContextRef = useRef(screenataPageContext)
  screenataPageContextRef.current = screenataPageContext

  const requestBodyRef = useRef(requestBody)
  requestBodyRef.current = requestBody
  const nextSendBodyRef = useRef<Record<string, unknown> | null>(null)
  const toolOutputAutoSubmittedRef = useRef(false)
  const shouldAutoSubmitToolOutputRef = useRef(false)

  // Stable ref for onStreamFinish so useChat onFinish always sees latest
  const onStreamFinishRef = useRef(props.onStreamFinish)
  onStreamFinishRef.current = props.onStreamFinish

  // The transport's `fetch` closure below is frozen at first mount: useChat
  // keeps a single `Chat` instance because its `id` is intentionally stable
  // (see the useChat `id` comment), so it ignores rebuilt transport memos.
  // Reading `currentThreadId` directly from that closure pinned
  // `body.threadId` to the thread that existed at mount — the previous
  // session's persisted thread — so every freshly-created "new chat" silently
  // resumed the old thread's server-side working memory and the agent kept
  // continuing the prior conversation. Mirror it into a ref read at fetch
  // time, exactly like the other send-time values above.
  const currentThreadIdRef = useRef(currentThreadId)
  currentThreadIdRef.current = currentThreadId

  const token = localStorage.getItem('auth:token')
  const workspaceId = currentWorkspaceId

  // Memoize transport to prevent re-initialization of useChat
  // Custom transport that sends only the last message to reduce bandwidth
  // Server loads full message history from database
  const transport = useMemo(() => {
    return new DefaultChatTransport({
      api,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Workspace-Id': workspaceId!,
      },
      credentials: 'include',
      // Override default behavior to send only last message
      fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
        const body = init?.body ? JSON.parse(init.body as string) : {}
        const pendingOneShotBody = nextSendBodyRef.current

        const latestRequestBody = requestBodyRef.current
        if (latestRequestBody) {
          Object.assign(body, latestRequestBody)
        }
        if (pendingOneShotBody) {
          Object.assign(body, pendingOneShotBody)
          nextSendBodyRef.current = null
        }

        if (!body.turnContext || typeof body.turnContext !== 'object') {
          body.turnContext = {}
        }

        // Merge current page context atom — server merges data additively
        if (screenataPageContextRef.current) {
          const atomData = { ...(screenataPageContextRef.current.data ?? {}) }
          if (body.blockId && !atomData.blockId) {
            atomData.blockId = body.blockId
          }
          body.turnContext = {
            ...body.turnContext,
            pageContext: {
              ...(body.turnContext.pageContext ?? {}),
              ...screenataPageContextRef.current,
              data: {
                ...(body.turnContext.pageContext?.data ?? {}),
                ...atomData,
              },
            },
          }
          if (
            screenataPageContextRef.current.data?.policySlug &&
            !body.policySlug
          ) {
            body.policySlug = screenataPageContextRef.current.data.policySlug
          }

          // Auto-load the page's primary skill (unless an explicit set was
          // already provided via requestBody, e.g. the runbook draft page).
          // Without this the orchestrator never loads the evidence skill — the
          // tools are always-on — and skips its Connect-integration routing.
          if (!body.turnContext.autoLoadSkills) {
            const pageSkills =
              PAGE_AUTOLOAD_SKILLS[screenataPageContextRef.current.page ?? '']
            if (pageSkills?.length) {
              body.turnContext = {
                ...body.turnContext,
                autoLoadSkills: pageSkills.map((name) => ({ name })),
              }
            }
          }
        }

        // Snapshot current page content at send-time for the agent's getPageContent tool
        if (getPageContentRef.current) {
          const content = getPageContentRef.current()
          body.turnContext = {
            ...body.turnContext,
            pageSnapshot: {
              ...(content ? { content } : {}),
              path: window.location.pathname,
              capturedAt: new Date().toISOString(),
            },
          }
          if (api.includes('/screenata-chat')) {
            delete body.pageContent
            delete body.pagePath
          } else {
            if (content) {
              body.pageContent = content
            }
            body.pagePath = window.location.pathname
          }
        }

        if (body.programId && typeof body.programId === 'string') {
          body.turnContext = {
            ...body.turnContext,
            programId: body.programId,
          }
        }

        if (import.meta.env.DEV) {
          const lastMsg = Array.isArray(body.messages)
            ? body.messages[body.messages.length - 1]
            : null
          setLastSentContext({
            turnContext: body.turnContext
              ? structuredClone(body.turnContext)
              : null,
            messageMetadata: lastMsg?.metadata
              ? structuredClone(lastMsg.metadata)
              : null,
          })
        }

        // Read the active thread from a ref — the closure value is frozen at
        // mount (see currentThreadIdRef above), which leaked prior sessions.
        const activeThreadId = currentThreadIdRef.current
        if (activeThreadId) {
          body.threadId = activeThreadId
        }

        // Ensure a stable thread ID exists even before DB thread bootstrap.
        // This is required for endpoints that keep server-side working memory
        // while clients send only the latest message. Use the bare draft id
        // (NOT a `session-`-prefixed variant): the auto-create effect mints
        // the real thread with this exact id, so once `currentThreadId`
        // catches up the next turn reads the same working-memory bucket and
        // continuity holds. A prefixed fallback would orphan turn 1's memory
        // under an id the rehydrated thread never reuses.
        if (!body.threadId) {
          body.threadId = draftThreadIdRef.current
        }

        if (Array.isArray(body.messages)) {
          body.messages = selectRequestMessages(body.messages, sendOnlyLastMessage)
        }

        if (api.includes('/screenata-chat')) {
          console.log('[ScreenataChat][client] request body routing debug', {
            api,
            programId: body.programId,
            policySlug: body.policySlug,
            chatKind: body.chatKind,
            threadId: body.threadId,
            messageCount: Array.isArray(body.messages)
              ? body.messages.length
              : null,
            latestMessageText: Array.isArray(body.messages)
              ? (body.messages[body.messages.length - 1]?.parts ?? [])
                  .filter((part: any) => part?.type === 'text')
                  .map((part: any) => part.text)
                  .join('\\n')
              : null,
            requestBodyRef: latestRequestBody,
            pendingOneShotBody,
            hasTurnContextPageSnapshot: Boolean(body.turnContext?.pageSnapshot),
          })
        }

        const updatedInit = {
          ...init,
          body: JSON.stringify(body),
        }

        return fetch(input, updatedInit)
      },
    })
  }, [api, token, workspaceId, sendOnlyLastMessage])

  const chat = useChat<ChatUIMessage>({
    // Keep the useChat identity stable per session. Including
    // currentThreadId would cause useChat to reset its message state
    // every time the active thread changes (e.g. right after
    // auto-creating a thread from the first message), which wipes the
    // messages we just streamed. We manage message persistence via
    // ChatThreadManager ourselves, so a fixed id is sufficient.
    id: `chat-${chatInstanceIdRef.current}`,
    transport,
    // Auto-submit after approval — but only once per approval cycle.
    sendAutomaticallyWhen: ({ messages: msgs }) => {
      const approvalResult =
        lastAssistantMessageIsCompleteWithApprovalResponses({ messages: msgs })
      const toolOutputResult =
        shouldAutoSubmitToolOutputRef.current &&
        !toolOutputAutoSubmittedRef.current &&
        lastAssistantMessageIsCompleteWithToolCalls({ messages: msgs })
      if (!approvalResult && !toolOutputResult) return false
      if (toolOutputResult) {
        toolOutputAutoSubmittedRef.current = true
        shouldAutoSubmitToolOutputRef.current = false
        return true
      }
      // Guard: only fire once. After the first auto-submit, the approval
      // response becomes output-available and this condition stops matching.
      // But if it somehow fires again (stale state), block it.
      if (approvalAutoSubmittedRef.current) return false
      approvalAutoSubmittedRef.current = true
      return true
    },
    onError: (error) => {
      console.error('>>> chat error', error)

      // Cancellation is normal user flow (clicked Stop) — silently
      // swallow it. The partial assistant message already on screen is
      // the appropriate signal.
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        return
      }

      // Pick a friendly, conversational error string. The server's
      // `onError` (ai-router.ts) already returns a generic
      // "I hit a problem…" string for most failures; refine it
      // client-side for shapes we can detect (network / auth / rate
      // limit) and fall through to the server's text otherwise.
      let errorMessage =
        error.message ||
        'I hit a problem while drafting that reply. Try again, or rephrase what you need.'
      let surfaceAsToast = false
      if (
        error.message?.includes('network') ||
        error.message?.includes('fetch')
      ) {
        errorMessage =
          'Network error. Check your connection and try again.'
        // Network errors affect the whole page, not just this turn — a
        // toast is the appropriate channel.
        surfaceAsToast = true
      } else if (
        error.message?.includes('rate limit') ||
        error.message?.includes('quota')
      ) {
        errorMessage = 'Rate limit hit. Wait a moment and try again.'
        surfaceAsToast = true
      } else if (
        error.message?.includes('unauthorized') ||
        error.message?.includes('401')
      ) {
        errorMessage = 'Auth error. Refresh the page and sign in again.'
        surfaceAsToast = true
      }

      // Append a synthetic assistant message so the error appears
      // INLINE in the conversation flow — the user can keep typing,
      // the chat doesn't die. Previously we set `chatError` (which
      // rendered a modal-style red banner above the input) plus a
      // toast; both interrupted the conversation and left the partial
      // assistant message stranded. Inline error + continue is the
      // claude-code-style recovery pattern.
      setMessagesRef.current?.((current) => [
        ...current,
        {
          id: `chat-error-${Date.now()}`,
          role: 'assistant',
          // Flag the synthetic error so the UI can surface a prominent
          // "Retry" affordance (re-runs the failed turn) rather than only
          // the subtle hover regenerate icon.
          metadata: { isError: true },
          parts: [{ type: 'text', text: errorMessage }],
        } as ChatUIMessage,
      ])

      if (surfaceAsToast) {
        toast.error(errorMessage)
      }
    },
    onFinish: (result) => {
      // Clear any existing errors when a request completes successfully
      setChatError(null)

      // RFC-070: Capture RAG sources from message metadata (AI SDK v5)
      const message = result.message
      console.log('[Chat] RFC-070: onFinish called', {
        messageId: message?.id,
        hasMetadata: !!message?.metadata,
        metadata: message?.metadata,
      })

      // RFC-070: Extract ragSources from metadata (sent on 'start' part)
      const ragSources = message?.metadata?.ragSources

      if (ragSources && Array.isArray(ragSources)) {
        setMessageSources((prev) => {
          const newMap = new Map(prev)
          newMap.set(message.id, ragSources)
          console.log('[Chat] RFC-070: Captured RAG sources from metadata:', {
            messageId: message.id,
            sourceCount: ragSources.length,
            sources: ragSources,
          })
          return newMap
        })
      } else {
        console.warn('[Chat] RFC-070: No ragSources found in metadata', {
          messageId: message?.id,
          hasMetadata: !!message?.metadata,
          metadata: message?.metadata,
        })
      }

      // Notify parent that the stream has finished (server post-processing is done)
      onStreamFinishRef.current?.(message)
    },
  })

  const {
    status: chatStatus,
    messages,
    setMessages,
    error,
    sendMessage,
    regenerate,
    stop,
    addToolOutput,
    addToolApprovalResponse,
  } = chat
  // Plumb `setMessages` back into the ref the `onError` closure (above,
  // line ~1187) captured. Stable identity from the SDK, so this writes
  // once per useChat instance.
  setMessagesRef.current = setMessages
  // Welcome layout (big title + stats + action items) only shows on the
  // dedicated Screenata chat home — never in a docked side-rail on a
  // content page. Side panels must stay minimal.
  const forceIdleWelcomeLayout =
    props.forceWelcomeLayout &&
    chatStatus === 'ready' &&
    (messages.length === 0 || messages === staleMessagesRef.current)
  const isWelcomeLayout =
    !routedThreadId &&
    !isOnboardingChat &&
    (forceIdleWelcomeLayout ||
      (messages.length === 0 &&
        !pageContext?.page &&
        isScreenataChatViewActive))
  const artifactPanelState = useAtomValue(artifactPanelStateAtom)
  const setArtifactPanel = useSetAtom(artifactPanelStateAtom)
  // Derive the pending presentOptions prompt directly from the message
  // stream: walk backwards, find the last user message, then the last
  // tool-presentOptions call after it. If the last message is still from
  // the user, or the assistant's last turn ended without a presentOptions
  // tool call, there is no pending prompt. A local "dismissed" id lets the
  // user close the card without reloading.
  const [dismissedPromptId, setDismissedPromptId] = useState<string | null>(
    null
  )
  const pendingOptions = useMemo<PendingOptionsPrompt | null>(() => {
    let lastUserIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        lastUserIdx = i
        break
      }
    }
    for (let i = messages.length - 1; i > lastUserIdx; i--) {
      const msg = messages[i]
      if (!msg || msg.role !== 'assistant') continue
      const parts = (msg.parts ?? []) as any[]
      for (let j = parts.length - 1; j >= 0; j--) {
        const p = parts[j]
        // Both presentOptions and presentQuestion render via the same
        // component and both can carry options — walk them equally.
        if (
          p?.type !== 'tool-presentOptions' &&
          p?.type !== 'tool-presentQuestion'
        ) {
          continue
        }
        // Wait for the complete tool input before mounting the card. Partial
        // input changes throughout the stream and would repeatedly resize the
        // composer area, but a complete input can render without waiting for
        // the SSE connection itself to close.
        if (
          p.state === 'input-streaming' ||
          p.toolInvocation?.state === 'partial-call'
        ) {
          return null
        }
        // presentQuestion/presentOptions: the server execute() looks up
        // the full question + options by `questionKey` and returns the
        // resolved payload as `output`. The `input` only has the bare
        // questionKey (no options), so we must prefer output when it's
        // available. Fall back to input / toolInvocation for historical
        // or legacy shapes.
        let data: any = null
        try {
          if (p.output != null) {
            data =
              typeof p.output === 'string' ? JSON.parse(p.output) : p.output
          } else if (p.toolInvocation?.result) {
            data =
              typeof p.toolInvocation.result === 'string'
                ? JSON.parse(p.toolInvocation.result)
                : p.toolInvocation.result
          } else if (p.input) {
            data = typeof p.input === 'string' ? JSON.parse(p.input) : p.input
          } else if (p.toolInvocation?.args) {
            data = p.toolInvocation.args
          }
        } catch {
          data = null
        }
        if (!data?.question) continue
        const hasOptions = data.options && data.options.length > 0
        const hasAllowOther = data.allowOther !== false
        if (!hasOptions && !hasAllowOther) continue
        const toolCallId: string =
          p.toolCallId ?? p.toolInvocation?.toolCallId ?? ''
        if (!toolCallId || toolCallId === dismissedPromptId) return null
        // Back-compat: presentOptions accepts string OR { label, description?, payload? }
        // items. Flatten into parallel arrays so existing renderers stay on string[].
        const rawOptions: unknown[] = Array.isArray(data.options)
          ? data.options
          : []
        const options: string[] = []
        const optionPayloads: Array<Record<string, unknown> | null> = []
        const optionDescriptions: Array<string | null> = []
        for (const o of rawOptions) {
          if (typeof o === 'string') {
            options.push(o)
            optionPayloads.push(null)
            optionDescriptions.push(null)
          } else if (o && typeof o === 'object') {
            const oo = o as Record<string, unknown>
            options.push(typeof oo.label === 'string' ? oo.label : String(oo.label ?? ''))
            optionPayloads.push(
              oo.payload && typeof oo.payload === 'object'
                ? (oo.payload as Record<string, unknown>)
                : null
            )
            optionDescriptions.push(
              typeof oo.description === 'string' ? oo.description : null
            )
          }
        }
        return {
          toolCallId,
          toolName:
            p.type === 'tool-presentOptions'
              ? 'presentOptions'
              : 'presentQuestion',
          question: data.question,
          explanation: data.explanation ?? null,
          placeholder: data.placeholder ?? null,
          options,
          optionPayloads: optionPayloads.some((p) => p) ? optionPayloads : null,
          optionDescriptions: optionDescriptions.some((d) => d)
            ? optionDescriptions
            : null,
          optionsMeta: data.optionsMeta ?? null,
          recommendedIndex: data.recommendedIndex ?? null,
          multiSelect: !!data.multiSelect,
          scope: data.scope,
          activityContext: {
            questionId: data.questionId ?? null,
            blueprintInstanceIds: data.blueprintInstanceIds ?? [],
            sectionKey: data.sectionKey ?? null,
          },
        } satisfies PendingOptionsPrompt
      }
    }
    return null
  }, [messages, dismissedPromptId])
  const pendingOptionsToolCallId = pendingOptions?.toolCallId ?? null
  const pendingOptionsOptionCount = pendingOptions?.options.length ?? null
  const canShowDeferredOptionsDuringStreaming =
    chatEventScope === 'screenata-chat' &&
    pendingOptions?.toolName === 'presentOptions'
  const shouldShowPendingOptions =
    pendingOptions !== null &&
    chatStatus !== 'submitted' &&
    (chatStatus !== 'streaming' || canShowDeferredOptionsDuringStreaming)
  const latestMessageId = messages[messages.length - 1]?.id ?? null
  const latestMessageRole = messages[messages.length - 1]?.role ?? null

  // #1270 — a pending `presentForm` deferred tool: the model asked for
  // structured input. Mirrors the pendingOptions walker — the form spec is the
  // model's tool INPUT (no execute), and we render a form that resolves the
  // tool via addToolOutput. Skipped once `output` is set (already submitted).
  const pendingForm = useMemo<PendingFormPrompt | null>(() => {
    let lastUserIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        lastUserIdx = i
        break
      }
    }
    for (let i = messages.length - 1; i > lastUserIdx; i--) {
      const msg = messages[i]
      if (!msg || msg.role !== 'assistant') continue
      const parts = (msg.parts ?? []) as any[]
      for (let j = parts.length - 1; j >= 0; j--) {
        const p = parts[j]
        if (p?.type !== 'tool-presentForm') continue
        if (p.output != null || p.toolInvocation?.result != null) return null
        if (p.state !== 'input-available') return null
        let data: any = null
        try {
          const raw = p.input ?? p.toolInvocation?.args
          data = typeof raw === 'string' ? JSON.parse(raw) : raw
        } catch {
          data = null
        }
        if (
          !data?.title ||
          !Array.isArray(data.fields) ||
          data.fields.length === 0
        ) {
          continue
        }
        const toolCallId: string =
          p.toolCallId ?? p.toolInvocation?.toolCallId ?? ''
        if (!toolCallId || toolCallId === dismissedPromptId) return null
        return {
          toolCallId,
          spec: {
            title: String(data.title),
            intro: data.intro ?? null,
            submitLabel: data.submitLabel ?? null,
            fields: data.fields,
          },
        } satisfies PendingFormPrompt
      }
    }
    return null
  }, [messages, dismissedPromptId])

  // Derive a pending sidecar action from the same walker: tools that
  // open an artifact panel (connectGithub, presentVendorPicker,
  // presentComplianceRoles) render as a button panel overlaying the
  // PromptInput instead of as an inline chat bubble. User clicks "Open"
  // to set the artifact state, or "Skip" to dismiss.
  const pendingSidecarAction = useMemo<{
    toolCallId: string
    title: string
    description: string
    openLabel: string
    artifactState: Exclude<typeof artifactPanelState, null>
  } | null>(() => {
    let lastUserIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        lastUserIdx = i
        break
      }
    }
    const sidecarToolTypes = new Set([
      'tool-connectGithub',
      'tool-triggerGithubScan',
      'tool-presentVendorPicker',
      'tool-presentComplianceRoles',
    ])
    for (let i = messages.length - 1; i > lastUserIdx; i--) {
      const msg = messages[i]
      if (!msg || msg.role !== 'assistant') continue
      const parts = (msg.parts ?? []) as any[]
      for (let j = parts.length - 1; j >= 0; j--) {
        const p = parts[j]
        if (!sidecarToolTypes.has(p?.type)) continue
        let data: any = null
        try {
          if (p.output != null) {
            data =
              typeof p.output === 'string' ? JSON.parse(p.output) : p.output
          } else if (p.toolInvocation?.result) {
            data =
              typeof p.toolInvocation.result === 'string'
                ? JSON.parse(p.toolInvocation.result)
                : p.toolInvocation.result
          } else if (p.input) {
            data = typeof p.input === 'string' ? JSON.parse(p.input) : p.input
          }
        } catch {
          data = null
        }
        const toolCallId: string =
          p.toolCallId ?? p.toolInvocation?.toolCallId ?? ''
        if (!toolCallId || toolCallId === dismissedPromptId) return null
        if (
          p.type === 'tool-connectGithub' ||
          p.type === 'tool-triggerGithubScan'
        ) {
          return {
            toolCallId,
            title: 'Connect GitHub',
            description:
              'Install the Screenata GitHub App, then scan repos for compliance evidence.',
            openLabel: 'Open GitHub connector',
            artifactState: { type: 'connect-github' },
          }
        }
        if (p.type === 'tool-presentVendorPicker' && data?.categories) {
          return {
            toolCallId,
            title: 'Pick your vendors',
            description: 'Search the catalog and select the vendors you use.',
            openLabel: 'Open vendor picker',
            artifactState: {
              type: 'vendor-picker',
              data: {
                toolCallId,
                question: data.question,
                categories: data.categories,
                vendorLogos: data.vendorLogos,
                preSelected: data.preSelected,
                scope: data.scope,
              },
            },
          }
        }
        if (p.type === 'tool-presentComplianceRoles' && data?.roles) {
          return {
            toolCallId,
            title: 'Set up compliance roles',
            description: 'Confirm who owns each security responsibility.',
            openLabel: 'Open role editor',
            artifactState: {
              type: 'compliance-roles',
              data: {
                toolCallId,
                question: data.question,
                roles: data.roles,
                scope: data.scope,
              },
            },
          }
        }
      }
    }
    return null
  }, [messages, dismissedPromptId])
  const [multiSelected, setMultiSelected] = useState<Set<string>>(new Set())
  const [pendingOptionsOtherText, setPendingOptionsOtherText] = useState('')
  const [activeOptionIndex, setActiveOptionIndex] = useState(0)
  const promptInputTextareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingOptionsCardRef = useRef<HTMLDivElement>(null)
  const pendingOptionButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const pendingOptionsSendButtonRef = useRef<HTMLButtonElement | null>(null)
  const pendingOptionsOtherInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    setMultiSelected(new Set())
    setPendingOptionsOtherText('')
    setActiveOptionIndex(0)
    pendingOptionButtonRefs.current = []
  }, [pendingOptionsToolCallId, pendingOptionsOptionCount])
  useEffect(() => {
    const activeButton = pendingOptionButtonRefs.current[activeOptionIndex]
    if (!activeButton) return
    activeButton.scrollIntoView({ block: 'nearest' })
  }, [activeOptionIndex])
  useEffect(() => {
    if (isWelcomeLayout || !shouldShowPendingOptions) {
      setPendingOptionsPrompt(null)
      return
    }

    setPendingOptionsPrompt(pendingOptions)
    return () => setPendingOptionsPrompt(null)
  }, [
    isWelcomeLayout,
    pendingOptions,
    setPendingOptionsPrompt,
    shouldShowPendingOptions,
  ])
  // Autofocus the appropriate target whenever the assistant turn ends on
  // a question — either the PromptInput textarea (freeform) or the
  // pending-options card so keyboard shortcuts work immediately.
  useEffect(() => {
    if (chatStatus !== 'ready') return
    if (isWelcomeLayout) return
    if (!latestMessageId || latestMessageRole !== 'assistant') return
    const target = pendingOptionsToolCallId
      ? pendingOptionsOptionCount === 0
        ? pendingOptionsOtherInputRef.current
        : pendingOptionsCardRef.current
      : promptInputTextareaRef.current
    if (!target) return
    const id = window.setTimeout(() => {
      if (pendingOptionsCardRef.current?.contains(document.activeElement))
        return
      target.focus({ preventScroll: true })
    }, 50)
    return () => window.clearTimeout(id)
  }, [
    chatStatus,
    isWelcomeLayout,
    latestMessageId,
    latestMessageRole,
    pendingOptionsOptionCount,
    pendingOptionsToolCallId,
  ])
  const handlePendingOptionsKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement> | KeyboardEvent) => {
      if (
        isWelcomeLayout ||
        !pendingOptions ||
        pendingOptions.options.length === 0
      )
        return
      if (event.defaultPrevented) return

      const target = event.target instanceof Element ? event.target : null
      const isTextInput = Boolean(
        target?.closest('input, textarea, [contenteditable="true"]')
      )
      if (
        isTextInput &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp')
      ) {
        return
      }

      if (
        pendingOptions.multiSelect &&
        event.key === 'Enter' &&
        event.shiftKey
      ) {
        event.preventDefault()
        if (multiSelected.size === 0) return
        pendingOptionsSendButtonRef.current?.click()
        return
      }
      if (isTextInput) return
      if (event.key === 'Enter' && target?.closest('button')) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        pendingOptionsCardRef.current?.focus()
        setActiveOptionIndex(
          (current) => (current + 1) % pendingOptions.options.length
        )
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        pendingOptionsCardRef.current?.focus()
        setActiveOptionIndex(
          (current) =>
            (current - 1 + pendingOptions.options.length) %
            pendingOptions.options.length
        )
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        pendingOptionButtonRefs.current[activeOptionIndex]?.click()
      }
    },
    [activeOptionIndex, isWelcomeLayout, multiSelected.size, pendingOptions]
  )
  useEffect(() => {
    if (isWelcomeLayout || !shouldShowPendingOptions) return

    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (
        event.target instanceof Node &&
        pendingOptionsCardRef.current?.contains(event.target)
      ) {
        return
      }
      handlePendingOptionsKeyDown(event)
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handlePendingOptionsKeyDown, isWelcomeLayout, shouldShowPendingOptions])
  const deferredMessages = useDeferredValue(messages)
  const deferredMessageSources = useDeferredValue(messageSources)

  // Notify parent of message changes (e.g., for detecting tool results)
  const onMessagesChange = props.onMessagesChange
  useEffect(() => {
    if (!onMessagesChange) return
    onMessagesChange(messages)
  }, [messages, onMessagesChange])

  // Auto-start: send an initial message when the chat opens.
  // Accepts either a plain string or { message, body? }. The optional body is
  // stashed for the next transport fetch so one-shot routing hints like
  // chatKind survive SDK/body merge timing.
  const hasAutoStarted = useRef(false)
  useEffect(() => {
    if (!props.autoStartMessage || hasAutoStarted.current) return
    const autoStart =
      typeof props.autoStartMessage === 'string'
        ? {
            message: props.autoStartMessage,
            body: undefined as Record<string, unknown> | undefined,
          }
        : props.autoStartMessage
    if (!autoStart.message) return
    hasAutoStarted.current = true
    // Mark this thread as already loaded so the thread-loading effect
    // doesn't wipe the messages after autoStartMessage is cleared.
    if (currentThreadId) {
      lastLoadedThreadId.current = currentThreadId
    }
    setTimeout(() => {
      nextSendBodyRef.current = autoStart.body ?? null
      sendMessage(
        {
          role: 'user',
          parts: [{ type: 'text', text: autoStart.message }],
        },
        undefined
      )
    }, 100)
  }, [props.autoStartMessage, sendMessage, currentThreadId])

  // Listen for programmatic messages dispatched via chatProgrammaticMessageAtom
  const programmaticMessage = useAtomValue(chatProgrammaticMessageAtom)
  const setProgrammaticMessage = useSetAtom(chatProgrammaticMessageAtom)
  useEffect(() => {
    if (!programmaticMessage) return
    if (programmaticMessage.draft) {
      setInput(programmaticMessage.text)
      setProgrammaticMessage(null)
      promptInputTextareaRef.current?.focus()
      return
    }
    console.log('[Chat] Received programmatic message:', programmaticMessage)
    const {
      text,
      activityContext,
      body: messageBody,
      metadata,
    } = programmaticMessage
    const askVeraThreadId = currentThreadIdRef.current
    if (metadata?.source === 'ask-vera' && askVeraThreadId) {
      programmaticThreadIdRef.current = askVeraThreadId
      lastLoadedThreadId.current = askVeraThreadId
    }
    setProgrammaticMessage(null)
    const requestBody = messageBody
      ? activityContext
        ? { ...messageBody, ...wrapActivityContext(activityContext) }
        : messageBody
      : activityContext
        ? wrapActivityContext(activityContext)
        : undefined
    console.log('[Chat] requestBody constructed', {
      activityContext: activityContext,
      wrapped: wrapActivityContext(activityContext ?? {}),
      'turnContext.pageContext.data':
        requestBody?.turnContext?.pageContext?.data,
      claimId: requestBody?.turnContext?.pageContext?.data?.claimId,
    })
    sendMessage(
      {
        role: 'user',
        parts: [{ type: 'text', text }],
        ...(metadata ? { metadata: metadata as any } : {}),
      },
      requestBody ? { body: requestBody } : undefined
    )
  }, [programmaticMessage, setProgrammaticMessage, sendMessage])

  // Listen for tool approval responses (needsApproval human-in-the-loop).
  // Approval resumes the stream; denial records the response and leaves
  // the user in control.
  // Stable refs to avoid tearing down/re-adding the listener on every render
  const addToolApprovalResponseRef = useRef(addToolApprovalResponse)
  addToolApprovalResponseRef.current = addToolApprovalResponse
  const addToolOutputRef = useRef(addToolOutput)
  addToolOutputRef.current = addToolOutput
  // Guard: prevent sendAutomaticallyWhen from firing more than once per approval
  const approvalAutoSubmittedRef = useRef(false)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { approvalId?: string; approved?: boolean }
        | undefined
      if (!detail?.approvalId || typeof detail.approved !== 'boolean') return
      approvalAutoSubmittedRef.current = !detail.approved
      addToolApprovalResponseRef.current({
        id: detail.approvalId,
        approved: detail.approved,
      })
    }
    window.addEventListener('screenata:tool-approval', handler)
    return () => window.removeEventListener('screenata:tool-approval', handler)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | {
            tool?: string
            toolCallId?: string
            output?: unknown
            scope?: string
            autoResume?: boolean
          }
        | undefined
      if (!detail?.toolCallId || !detail.tool) return
      if (chatEventScope && detail.scope && detail.scope !== chatEventScope) {
        return
      }
      if (chatEventScope && !detail.scope) return
      // Record the deferred tool's result so the tool_call isn't left
      // orphaned — but only kick off a fresh agent turn when the caller
      // wants it. A dismissal (`autoResume: false`) resolves the tool and
      // closes the card WITHOUT resuming; otherwise the agent re-explores
      // and re-asks in a loop after every "Skip". The dismissed result
      // still rides along with the user's next message.
      if (detail.autoResume !== false) {
        shouldAutoSubmitToolOutputRef.current = true
        toolOutputAutoSubmittedRef.current = false
      }
      addToolOutputRef.current({
        tool: detail.tool,
        toolCallId: detail.toolCallId,
        output: detail.output,
      } as Parameters<typeof addToolOutput>[0])
    }
    window.addEventListener('screenata:tool-output', handler)
    return () => window.removeEventListener('screenata:tool-output', handler)
  }, [chatEventScope])

  // Submit the user's answer to a pending presentOptions popover. The
  // `presentOptions` tool is deferred (no server-side execute) — its
  // resolution path is the same `screenata:tool-output` event the
  // `askQuestions` tool uses (see RunbookDraftToolMessages.tsx). Dispatching
  // the event keeps SDK plumbing (addToolOutput + the auto-resume refs) in
  // ONE listener. `presentQuestion` is a separate tool and still uses the
  // legacy user-message flow.
  const submitPresentOptions = useCallback(
    (args: {
      prompt: PendingOptionsPrompt
      status: 'answered' | 'dismissed'
      selected?: string | string[] | null
      selectedIndexes?: number[]
      freeform?: boolean
      activityContext: Record<string, unknown>
      fallbackText: string
    }) => {
      const {
        prompt,
        status,
        selected,
        selectedIndexes,
        freeform,
        activityContext,
        fallbackText,
      } = args
      setDismissedPromptId(prompt.toolCallId)
      if (prompt.toolName === 'presentOptions') {
        const payloads = selectedIndexes
          ?.map((i) => prompt.optionPayloads?.[i] ?? null)
          .filter(Boolean) as Array<Record<string, unknown>> | undefined
        const output: Record<string, unknown> = {
          status,
          selected: selected ?? null,
          freeform: freeform ?? false,
          message: formatPresentOptionsMessage(status, selected),
        }
        if (payloads && payloads.length > 0) {
          output.payload = payloads.length === 1 ? payloads[0] : payloads
        }
        window.dispatchEvent(
          new CustomEvent('screenata:tool-output', {
            detail: {
              scope: chatEventScope,
              tool: 'presentOptions',
              toolCallId: prompt.toolCallId,
              output,
              // Dismissal closes the card and stops — don't auto-resume a
              // new agent turn (that produced an explore→re-ask loop).
              autoResume: status !== 'dismissed',
            },
          })
        )
        return
      }
      // Legacy presentQuestion path: the answer is a plain user message.
      // On dismissal, just close the card — sending "do not proceed" as a
      // user message would start a turn the user explicitly opted out of.
      if (status === 'dismissed') return
      sendMessage(
        { role: 'user', parts: [{ type: 'text', text: fallbackText }] },
        { body: wrapActivityContext(activityContext) }
      )
    },
    [chatEventScope, sendMessage]
  )

  // #1270 — resolve a `presentForm` deferred tool with the user's values (or a
  // cancel). Routes back through the shared `screenata:tool-output` listener →
  // addToolOutput, so the agent re-calls the original tool with the values.
  const submitPresentForm = useCallback(
    (args: {
      prompt: PendingFormPrompt
      status: 'submitted' | 'cancelled'
      values: Record<string, unknown>
    }) => {
      const { prompt, status, values } = args
      setDismissedPromptId(prompt.toolCallId)
      window.dispatchEvent(
        new CustomEvent('screenata:tool-output', {
          detail: {
            scope: chatEventScope,
            tool: 'presentForm',
            toolCallId: prompt.toolCallId,
            output: {
              status,
              values: status === 'submitted' ? values : {},
              message:
                status === 'submitted'
                  ? 'User submitted the form.'
                  : 'User cancelled the form.',
            },
            // Cancel closes the card without resuming (mirrors options dismiss).
            autoResume: status !== 'cancelled',
          },
        })
      )
    },
    [chatEventScope]
  )

  // Listen for option-selected events from PresentOptionsMessage
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | {
            text?: string
            scope?: string
            activityContext?: Record<string, unknown>
          }
        | undefined
      const text = detail?.text
      const scope = detail?.scope
      if (!text) return
      if (chatEventScope && scope && scope !== chatEventScope) return
      if (chatEventScope && !scope) return
      sendMessage(
        {
          role: 'user',
          parts: [{ type: 'text', text }],
        },
        detail?.activityContext
          ? { body: wrapActivityContext(detail.activityContext) }
          : undefined
      )
      window.dispatchEvent(
        new CustomEvent('screenata:chat-scroll-bottom', {
          detail: { scope: chatEventScope },
        })
      )
    }
    window.addEventListener('screenata:option-selected', handler)
    return () =>
      window.removeEventListener('screenata:option-selected', handler)
  }, [chatEventScope, sendMessage])

  // Note: Removed manual setMessages call that was interfering with SDK state
  // The SDK should manage its own message state lifecycle

  const unifiedStatus = useMemo(() => {
    return isGenerationMode && generationStatus !== 'completed'
      ? initialStatus
      : chatStatus
  }, [chatStatus, initialStatus, isGenerationMode, generationStatus])
  const isStreamingResponse =
    unifiedStatus === 'streaming' || unifiedStatus === 'submitted'
  // The thread loaders below check `unifiedStatus` on entry, then `await`
  // localforage. Their closures hold the status from before that await, so
  // they need a live read to tell whether a turn started while they were
  // reading. See the staleness guards in loadThreadMessages.
  const unifiedStatusRef = useRef(unifiedStatus)
  unifiedStatusRef.current = unifiedStatus

  useEffect(() => {
    if (isGenerationMode) {
      // Generation mode (e.g. policy drafting) doesn't have a chat
      // surface to append synthetic messages into, so the banner stays
      // its primary error channel.
      setChatError(generationError?.message ?? null)
    } else {
      // Chat mode: `onError` (above) appends a synthetic assistant
      // message with the error text, so the user sees the failure
      // inline in the conversation. Skip mirroring `chat.error` to
      // `chatError` — the red banner would duplicate the inline
      // message and re-introduce the "bailed out" feel we just fixed.
    }
  }, [error, generationError, isGenerationMode])

  // Load generation thread from sessionStorage
  const hasCheckedSessionStorage = useRef(false)

  useEffect(() => {
    // Check sessionStorage when component mounts or when status becomes ready without a thread
    const shouldCheck =
      !hasCheckedSessionStorage.current ||
      (unifiedStatus === 'ready' && !currentThreadId && !isGenerationMode)

    if (!shouldCheck) return

    const generationThreadId = sessionStorage.getItem('generationThreadId')
    console.log(
      '>>> Checking sessionStorage for generationThreadId:',
      generationThreadId,
      'currentThreadId:',
      currentThreadId,
      'status:',
      unifiedStatus
    )

    if (generationThreadId && !currentThreadId) {
      console.log(
        '>>> Loading generation thread from sessionStorage:',
        generationThreadId
      )
      hasCheckedSessionStorage.current = true
      setCurrentThreadId(generationThreadId)

      // Load thread messages into chat SDK
      const loadThreadMessages = async () => {
        try {
          isLoadingExistingMessages.current = true

          // Load all threads to ensure we have the complete list including the generation thread
          console.log('>>> Loading all threads including generation thread')
          await loadThreads()

          const existingMessages =
            await ChatThreadManager.getMessages(generationThreadId)
          console.log(
            '>>> Loading generation thread messages:',
            existingMessages.length
          )
          // Same staleness rule as the thread-selection loader: never write
          // stored history over a turn that started while we were reading.
          if (unifiedStatusRef.current !== 'ready') return
          if (existingMessages.length > 0) {
            setMessages(existingMessages)

            // Suggest thread name for generation thread
            // console.log('>>> Suggesting thread name for generation thread')
            // console.log('>>> existingMessages full:', existingMessages)
            // console.log(
            //   '>>> existingMessages.slice(1) full:',
            //   existingMessages.slice(1)
            // )
            // Thread name suggestion now handled by ChatThreadHeader component
          }
        } catch (error) {
          console.error('>>> Failed to load generation thread messages:', error)
        } finally {
          isLoadingExistingMessages.current = false
        }
      }

      loadThreadMessages()

      // Clear sessionStorage after loading (one-time use)
      sessionStorage.removeItem('generationThreadId')
      // console.log('>>> Cleared generation thread ID from sessionStorage')
    } else if (generationThreadId && currentThreadId) {
      console.log(
        '>>> Generation thread found but currentThreadId already set:',
        currentThreadId
      )
      hasCheckedSessionStorage.current = true
    } else if (!generationThreadId) {
      console.log('>>> No generation thread found in sessionStorage')
      hasCheckedSessionStorage.current = true
    }
  }, [
    currentThreadId,
    setCurrentThreadId,
    setMessages,
    unifiedStatus,
    isGenerationMode,
  ])

  // Load thread messages when currentThreadId changes (for thread list selection)
  const lastLoadedThreadId = useRef<string | null>(null)
  const programmaticThreadIdRef = useRef<string | null>(null)
  const hasAutoSentMessage = useRef<Set<string>>(new Set())
  const autoCreatedThreadId = useRef<string | null>(null)
  // Tracks the previous currentThreadId so we can detect a real
  // navigation (e.g. /chat/X → /chat) and wipe stale messages. A fresh
  // mount with currentThreadId already null must not trigger any
  // clearing or it would swallow the user's very first message.
  const prevThreadIdRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevThreadIdRef.current
    prevThreadIdRef.current = currentThreadId

    // Real navigation away from a thread → home: wipe useChat state.
    // Fresh mounts (prev === null, current === null) fall through and
    // leave user input alone.
    if (prev && !currentThreadId) {
      if (messages.length > 0 && !props.autoStartMessage) {
        // Force-persist the current messages to the outgoing thread
        // before clearing. syncThreadMessages is debounced 2s, so a
        // user who types and immediately navigates would lose their
        // conversation. flushThread bypasses the debounce.
        void ChatThreadManager.getThread(prev).then((existing) => {
          if (!existing) return
          void ChatThreadManager.flushThread({
            ...existing,
            messages,
            updatedAt: new Date().toISOString(),
          })
        })
        staleMessagesRef.current = messages
        setMessages([])
      }
      lastLoadedThreadId.current = null
      hasAutoCreatedThread.current = false
      autoCreatedThreadId.current = null
      draftThreadIdRef.current = nanoid()
      return
    }
    if (!currentThreadId) {
      // Cold /chat mount. Nothing to do — auto-create will handle the
      // first message, and the existing loader has no thread to load.
      return
    }
    // Only load messages if:
    // 1. We have a currentThreadId
    // 2. The thread ID has changed
    // 3. We're not in generation mode
    // 4. The status is ready
    if (
      currentThreadId === lastLoadedThreadId.current ||
      isGenerationMode ||
      unifiedStatus !== 'ready' ||
      // Skip thread loading when autoStartMessage is set — the chat will auto-send
      // a fresh message and we don't want loadThreadMessages to wipe it via setMessages([])
      props.autoStartMessage ||
      // Skip only when the currently-active thread is the one we just
      // auto-created in this session — its messages are already live in
      // the chat state, so loading from localforage would wipe them.
      // Switching to a DIFFERENT thread must still load from storage.
      (hasAutoCreatedThread.current &&
        autoCreatedThreadId.current === currentThreadId)
    ) {
      return
    }

    // console.log(
    //   '>>> Loading thread messages for currentThreadId:',
    //   currentThreadId
    // )
    lastLoadedThreadId.current = currentThreadId

    const loadThreadMessages = async () => {
      try {
        isLoadingExistingMessages.current = true

        // Clear current messages first to show loading state
        setMessages([])

        const existingMessages =
          await ChatThreadManager.getMessages(currentThreadId)
        const thread = await ChatThreadManager.getThread(currentThreadId)

        // A turn can begin while the two reads above are in flight. "Ask Vera"
        // opens the panel and sends on a 250ms timer, so a cold localforage
        // read straddles the send: writing stored history now would replace
        // the array that already holds the just-sent user message, and the
        // still-running stream would then re-append only the assistant turn —
        // leaving a reply with no visible question. Live state is the truth
        // mid-turn. Keep a fresh Ask Vera thread marked as loaded so the
        // ready transition does not replace its live messages with stored [].
        if (unifiedStatusRef.current !== 'ready') {
          lastLoadedThreadId.current =
            programmaticThreadIdRef.current === currentThreadId
              ? currentThreadId
              : null
          return
        }

        console.log(
          '>>> Loading thread messages:',
          existingMessages.length,
          'for thread:',
          currentThreadId,
          'has editorContext:',
          !!thread?.editorContext
        )

        if (existingMessages.length > 0) {
          setMessages(existingMessages)
        } else if (!thread) {
          // Issue #516 — currentThreadId atom persists in localStorage but
          // the localforage thread row can vanish (eviction, clear, other
          // device). Without clearing, the next send ships the orphaned id
          // and the server rehydrates ghost history.
          console.log(
            '>>> Stale localStorage threadId, clearing:',
            currentThreadId
          )
          setCurrentThreadId(null)
        } else if (
          thread?.editorContext?.initialPrompt &&
          existingMessages.length === 0 &&
          !hasAutoSentMessage.current.has(currentThreadId)
        ) {
          // Auto-send initial message for new threads with editor context
          console.log(
            '>>> Auto-sending initial editor context message:',
            thread.editorContext.initialPrompt
          )
          hasAutoSentMessage.current.add(currentThreadId)

          setTimeout(() => {
            console.log(
              '[Chat] Calling append with initial prompt:',
              thread.editorContext!.initialPrompt!
            )
            sendMessage(
              {
                role: 'user',
                parts: [
                  {
                    type: 'text',
                    text: thread.editorContext!.initialPrompt!,
                  },
                ],
              },
              {
                body: {
                  editorContext: thread.editorContext,
                },
              }
            )
          }, 100)
        } else if (existingMessages.length === 0) {
          console.log('>>> No messages found for thread:', currentThreadId)
        }
      } catch (error) {
        console.error('>>> Failed to load thread messages:', error)
      } finally {
        isLoadingExistingMessages.current = false
      }
    }

    loadThreadMessages()
  }, [
    currentThreadId,
    setMessages,
    isGenerationMode,
    unifiedStatus,
    sendMessage,
    setCurrentThreadId,
  ])

  // Note: Thread management for generation is now handled deterministically
  // in handleGenerationComplete before navigation

  const handleNewThread = useCallback(async () => {
    try {
      // Generate new session ID for new chat
      draftThreadIdRef.current = nanoid()

      const thread = await createThread({
        name: 'New Chat',
        topicId: topic?.id,
        docId: topic?.docId,
        scope: currentWorkspaceId
          ? { workspaceId: currentWorkspaceId }
          : undefined,
      })
      console.log('>>> new thread', thread.messages)
      setChatError(null)
      setShowThreadList(false)
    } catch (error) {
      console.error('Failed to create new thread:', error)
    }
  }, [createThread, currentWorkspaceId, setShowThreadList, topic])

  const handleChatHistory = useCallback(() => {
    setShowThreadList(true)
  }, [])

  const handleRegenerate = useCallback(() => {
    // A regenerate re-sends the current message list; with nothing loaded
    // (e.g. a thread that hasn't hydrated, or right after a thread switch
    // clears messages) it POSTs an empty `messages` array and the server
    // 400s ("messages array is required"). Guard it.
    if (messages.length === 0) {
      toast.error('Nothing to regenerate yet. Send a message to continue.')
      return
    }
    try {
      regenerate()
    } catch (err) {
      console.error('[Chat] regenerate failed:', err)
      toast.error('Failed to regenerate. Please send a new message.')
    }
  }, [regenerate, messages.length])

  // Auto-create a thread as soon as the first message is sent (not waiting for response)
  const hasAutoCreatedThread = useRef(false)
  useEffect(() => {
    if (
      !currentThreadId &&
      !hasAutoCreatedThread.current &&
      messages.length >= 1
    ) {
      // Chat home may briefly hold stale messages from the previous surface
      // while route-scoped state catches up. Do not create a new thread from
      // those idle messages; a real user send leaves the ready state first.
      if (forceIdleWelcomeLayout) return

      // Thread-load effect (runs earlier in this commit) may have just
      // cleared the previous thread's messages via setMessages([]); its
      // closure here still holds the pre-clear array. Reference-match
      // against the `staleMessagesRef` we marked on that clear to skip
      // exactly one stale fire — the next render will have a fresh []
      // array from useChat and this guard naturally falls through.
      if (messages === staleMessagesRef.current) {
        staleMessagesRef.current = null
        return
      }
      staleMessagesRef.current = null
      hasAutoCreatedThread.current = true
      const draftThreadId = draftThreadIdRef.current
      autoCreatedThreadId.current = draftThreadId
      createThread({
        name: 'New chat',
        messages,
        id: draftThreadId,
        topicId: topic?.id,
        docId: topic?.docId,
        scope: currentWorkspaceId
          ? { workspaceId: currentWorkspaceId }
          : undefined,
      })
        .then(() => {
          lastLoadedThreadId.current = draftThreadId
          // Sync the URL to /chat/<newThreadId> so the thread is
          // deep-linkable. Docked side-rails on content pages must
          // leave their host URL alone.
          if (
            typeof window !== 'undefined' &&
            isWorkspaceChatPath(window.location.pathname)
          ) {
            const wsId = getWorkspaceIdFromPath(window.location.pathname)
            if (wsId) {
              void navigate({
                to: '/workspaces/$workspaceId/chat/$threadId',
                params: {
                  workspaceId: wsId,
                  threadId: draftThreadId,
                },
                replace: true,
              })
            }
          }
        })
        .catch((err) => console.error('Auto-create thread failed:', err))
    }
  }, [
    currentThreadId,
    forceIdleWelcomeLayout,
    messages.length,
    createThread,
    currentWorkspaceId,
    topic,
    messages,
  ])

  useEffect(() => {
    if (unifiedStatus === 'ready' && currentThreadId) {
      // Only sync messages if we're not currently loading existing messages
      // This prevents updatedAt from being modified when just opening a thread
      if (!isLoadingExistingMessages.current) {
        syncThreadMessages({
          threadId: currentThreadId,
          messages,
          workspaceId: currentWorkspaceId,
        })
        if (programmaticThreadIdRef.current === currentThreadId) {
          programmaticThreadIdRef.current = null
        }
      }
    }
  }, [
    unifiedStatus,
    currentThreadId,
    messages,
    syncThreadMessages,
    currentWorkspaceId,
  ])

  // console.log('>>> chat', chat.messages, initialMessages, unifiedStatus, new Date().toISOString())
  return (
    <ErrorBoundary
      fallbackRender={({
        resetErrorBoundary,
      }: {
        resetErrorBoundary: () => void
      }) => (
        <ErrorStatus
          icon={
            <MessageSquareMore size={24} className='text-muted-foreground' />
          }
          description='There was an error. Please try again.'
          onRetry={() => {
            resetErrorBoundary()
            // Don't blind-regenerate when the message list is empty — that
            // POSTs an empty `messages` array and 400s, turning the error
            // boundary into a retry → 400 → error loop. Ask for a fresh
            // message instead.
            if (chat.messages.length === 0) {
              toast.error('Nothing to retry yet. Send a message to continue.')
              return
            }
            try {
              chat.regenerate()
            } catch (err) {
              console.error('[Chat] error boundary regenerate failed:', err)
              toast.error('Failed to retry. Please send a new message.')
            }
          }}
        />
      )}
    >
      {/*
        RFC-154 Phase B: onboarding chat now shows the same ChatHeader as
        every other chat. The unified layout (sidebar + main + chat panel)
        means the chat side should have visual parity with the artifact
        side — both get a slim header bar. Old hack was `!isOnboardingChat`
        which suppressed the header during the full-screen onboarding era.
      */}
      {!props.hideHeader && !isWelcomeLayout && (
        <div>
          <ChatHeader
            onNewThread={handleNewThread}
            onHistory={handleChatHistory}
            isHistoryOpen={showThreadList}
            onBack={() => setShowThreadList(false)}
            onClose={onClose}
            messages={messages}
            chatStatus={chatStatus}
            onUpdateThreadName={updateThreadName}
            mode={mode}
            onToggleMode={onToggleMode}
            onToggleExpand={onToggleExpand}
            isExpanded={isExpanded}
            hideCloseButton={props.hideCloseButton}
          />
        </div>
      )}

      {showThreadList && !props.hideHeader ? (
        <ChatThreadList
          workspaceId={currentWorkspaceId}
          onSelect={(threadId) => {
            setCurrentThreadId(threadId)
            setShowThreadList(false)
            // Screenata chat threads should always deep-link to the
            // fullscreen thread URL, even when selected from the docked rail.
            if (
              chatEventScope === 'screenata-chat' &&
              typeof window !== 'undefined'
            ) {
              const wsId =
                currentWorkspaceId ??
                getWorkspaceIdFromPath(window.location.pathname) ??
                localStorage.getItem('auth:activeWorkspaceId')
              if (wsId) {
                void navigate({
                  to: '/workspaces/$workspaceId/chat/$threadId',
                  params: { workspaceId: wsId, threadId },
                })
              }
            }
          }}
        />
      ) : (
        <div className={cn('relative min-h-0 w-full flex-1', isWelcomeLayout ? 'overflow-y-auto bg-background' : 'flex flex-col overflow-hidden bg-card')}>
          {/* TEMP(demo): debug ctx bar hidden for user demo — uncomment to restore
          {import.meta.env.DEV && (
            <PageContextDebugBar
              pageContext={pageContext}
              threadId={currentThreadId}
              lastSentContext={lastSentContext}
            />
          )} */}

          {/* Welcome layout: header → composer → body. Active only when the
              user lands on the Screenata chat home (no page context, no
              messages, not mid-onboarding). Header renders here; the body
              renders after the composer div below so the composer sits
              visually between title and stats/action-queue. */}
          {isWelcomeLayout ? (
            <ChatWelcomeHeader />
          ) : (
            /* Messages area with scroll */
            <Conversation className='relative flex-1 overflow-hidden'>
              <ChatScrollToBottomBridge scope={chatEventScope} />
              <CaptureFlowContext.Provider value={captureFlowContextValue}>
                <Messages
                  messages={
                    isOnboardingChat || isStreamingResponse
                      ? messages
                      : deferredMessages
                  }
                  status={unifiedStatus}
                  messageSources={
                    isOnboardingChat || isStreamingResponse
                      ? messageSources
                      : deferredMessageSources
                  }
                  chatEventScope={chatEventScope}
                  onRegenerate={handleRegenerate}
                />
              </CaptureFlowContext.Provider>
              <ConversationScrollButton />
            </Conversation>
          )}

          {/* Error message */}
          {chatError && !chatError.includes('cancelled') && (
            <div className='mx-auto mb-2 w-full max-w-3xl px-4'>
              <div className='border-destructive/30 text-destructive rounded-md border px-3 py-2 text-sm'>
                <div className='flex items-center gap-3'>
                  <span className='flex-1 leading-6 break-words'>
                    An error occurred. Please try again.
                  </span>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={handleRegenerate}
                    className='border-destructive/20 text-foreground hover:bg-destructive/5 h-8 shrink-0'
                  >
                    Retry
                  </Button>
                  <button
                    type='button'
                    onClick={() => setChatError(null)}
                    className='text-destructive/60 hover:text-destructive shrink-0'
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pending-tool option card — a Claude-style picker attached to
              the top of the PromptInput. Question in the header, numbered
              option rows, multi-select "Send N" in the footer, X to dismiss.
              Precedence: option card > persistent CTA banner > click-to-
              continue wedge. Only one slot renders at a time. */}
          {!isWelcomeLayout && pendingSidecarAction ? (
            <div className='mx-auto w-full max-w-3xl bg-card px-4 pt-2 pb-2'>
              <div className='overflow-hidden rounded-md border border-border bg-card shadow-[0_2px_16px_-8px_rgba(16,24,40,0.12)]'>
                <div className='flex items-start justify-between gap-3 border-b border-border-faint px-4 py-2.5 dark:border-border-surface'>
                  <div className='min-w-0 flex-1'>
                    <div className='truncate text-[13px] font-semibold text-text-secondary'>
                      {pendingSidecarAction.title}
                    </div>
                    <div className='truncate text-[11px] text-text-subtle'>
                      {pendingSidecarAction.description}
                    </div>
                  </div>
                </div>
                <div className='flex items-center justify-end gap-2 px-3 py-2'>
                  <button
                    type='button'
                    onClick={() => {
                      const text = "Skip, I'll do this later."
                      setDismissedPromptId(pendingSidecarAction.toolCallId)
                      window.dispatchEvent(
                        new CustomEvent('screenata:option-selected', {
                          detail: {
                            text,
                            scope: 'screenata-onboarding-chat',
                            activityContext: { selectedText: text },
                          },
                        })
                      )
                    }}
                    className='rounded-md border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-text-subtle transition-colors hover:border-border-divider hover:text-text-secondary'
                  >
                    Skip
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setArtifactPanel(pendingSidecarAction.artifactState)
                      setDismissedPromptId(pendingSidecarAction.toolCallId)
                      window.dispatchEvent(
                        new CustomEvent('screenata:option-selected', {
                          detail: {
                            text: pendingSidecarAction.openLabel,
                            scope: 'screenata-onboarding-chat',
                            activityContext: {
                              selectedText: pendingSidecarAction.openLabel,
                            },
                          },
                        })
                      )
                    }}
                    className='inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                  >
                    {pendingSidecarAction.openLabel}
                    <ArrowRight className='h-3 w-3' />
                  </button>
                </div>
              </div>
            </div>
          ) : !isWelcomeLayout && shouldShowPendingOptions && pendingOptions ? (
            <div
              key={pendingOptions.toolCallId}
              className='chat-options-enter mx-auto w-full max-w-3xl bg-card px-4 pt-2 pb-2'
            >
              <div
                ref={pendingOptionsCardRef}
                tabIndex={0}
                onKeyDown={handlePendingOptionsKeyDown}
                className='overflow-hidden rounded-md border border-border bg-card shadow-[0_2px_16px_-8px_rgba(16,24,40,0.12)] focus:outline-none'
              >
                <div className='flex items-start justify-between gap-3 border-b border-border-faint px-4 py-2.5'>
                  <div className='min-w-0 flex-1'>
                    <div className='text-[12px] leading-[1.45] font-semibold break-words whitespace-normal text-text-secondary'>
                      {pendingOptions.question}
                    </div>
                    {pendingOptions.options.length === 0 &&
                      pendingOptions.explanation && (
                        <div className='text-[10px] text-text-subtle'>
                          {pendingOptions.explanation}
                        </div>
                      )}
                    {pendingOptions.multiSelect && (
                      <div className='text-[10px] text-text-subtle'>
                        Pick one or more, then send.
                      </div>
                    )}
                  </div>
                  <button
                    type='button'
                    onClick={() => {
                      const prompt = pendingOptions
                      setMultiSelected(new Set())
                      submitPresentOptions({
                        prompt,
                        status: 'dismissed',
                        selected: null,
                        activityContext: {
                          action: 'dismiss-options',
                          toolCallId: prompt.toolCallId,
                        },
                        fallbackText:
                          'User dismissed the question, do not proceed',
                      })
                    }}
                    aria-label='Dismiss options'
                    className='flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-placeholder hover:bg-muted hover:text-text-secondary'
                  >
                    <XIcon className='h-3.5 w-3.5' />
                  </button>
                </div>
                {pendingOptions.options.length > 0 && (
                  <ScrollArea
                    type='always'
                    viewClassName='max-h-[260px] py-0 [&>div]:!block'
                  >
                    {pendingOptions.options.map((option, idx) => {
                      const meta = pendingOptions.optionsMeta?.[idx]
                      const isRecommended =
                        pendingOptions.recommendedIndex != null &&
                        idx === pendingOptions.recommendedIndex
                      const isMultiActive =
                        pendingOptions.multiSelect && multiSelected.has(option)
                      return (
                        <button
                          key={option}
                          ref={(node) => {
                            pendingOptionButtonRefs.current[idx] = node
                          }}
                          type='button'
                          onClick={() => {
                            if (pendingOptions.multiSelect) {
                              setMultiSelected((prev) => {
                                const next = new Set(prev)
                                if (next.has(option)) next.delete(option)
                                else next.add(option)
                                return next
                              })
                              return
                            }
                            const activityContext = {
                              ...(pendingOptions.activityContext ?? {}),
                              selectedText: option,
                            }
                            const action = (meta as any)?.action
                            if (action?.type === 'openArtifact') {
                              const artifact = action.artifact
                              if (artifact === 'connect-github') {
                                setArtifactPanel({ type: 'connect-github' })
                              } else if (artifact === 'onboarding-answers') {
                                setArtifactPanel({ type: 'onboarding-answers' })
                              } else if (artifact === 'policy-prep') {
                                setArtifactPanel({ type: 'policy-prep' })
                              } else if (artifact === 'connect-tools') {
                                setArtifactPanel({ type: 'connect-tools' })
                              }
                            }
                            submitPresentOptions({
                              prompt: pendingOptions,
                              status: 'answered',
                              selected: option,
                              selectedIndexes: [idx],
                              activityContext,
                              fallbackText: option,
                            })
                          }}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] transition-colors',
                            isMultiActive
                              ? 'bg-selected text-text-secondary'
                              : cn(
                                  'text-text-secondary hover:bg-surface-subtle',
                                  idx === activeOptionIndex &&
                                    'bg-control-hover-strong dark:bg-border'
                                )
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border text-[10px] font-semibold tabular-nums',
                              pendingOptions.multiSelect
                                ? isMultiActive
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border-divider bg-card text-text-placeholder'
                                : 'border-primary bg-primary text-primary-foreground'
                            )}
                          >
                            {pendingOptions.multiSelect && isMultiActive
                              ? '✓'
                              : idx + 1}
                          </span>
                          {meta?.logoUrl && (
                            <img
                              src={meta.logoUrl}
                              alt=''
                              className='h-4 w-4 shrink-0 rounded-sm object-contain'
                            />
                          )}
                          <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                            <span className='leading-[1.4] break-words whitespace-normal'>
                              {option}
                            </span>
                            {pendingOptions.optionDescriptions?.[idx] && (
                              <span className='text-[11px] leading-[1.4] break-words whitespace-normal text-text-subtle'>
                                {pendingOptions.optionDescriptions[idx]}
                              </span>
                            )}
                          </span>
                          {isRecommended && (
                            <span className='shrink-0 rounded-full bg-cobalt-xsubtle px-2 py-0.5 text-[9px] font-semibold tracking-wider text-cobalt-strong uppercase'>
                              Recommended
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </ScrollArea>
                )}
                <div className='flex items-center gap-2 border-t border-border-faint px-3 py-2 dark:border-border-divider'>
                  {pendingOptions.options.length > 0 && (
                    <PlusIcon className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
                  )}
                  <input
                    ref={pendingOptionsOtherInputRef}
                    type='text'
                    value={pendingOptionsOtherText}
                    onChange={(e) => setPendingOptionsOtherText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' || e.shiftKey) return
                      const text = pendingOptionsOtherText.trim()
                      if (!text) return
                      e.preventDefault()
                      const prompt = pendingOptions
                      const activityContext = {
                        ...(prompt.activityContext ?? {}),
                        selectedText: text,
                      }
                      setMultiSelected(new Set())
                      setPendingOptionsOtherText('')
                      submitPresentOptions({
                        prompt,
                        status: 'answered',
                        selected: text,
                        freeform: true,
                        activityContext,
                        fallbackText: text,
                      })
                    }}
                    placeholder={
                      pendingOptions.options.length === 0
                        ? pendingOptions.placeholder || 'Type your answer...'
                        : 'Something else'
                    }
                    className='flex-1 border-0 bg-transparent text-[13px] text-text-secondary placeholder:text-text-placeholder focus:outline-none'
                  />
                  {pendingOptions.multiSelect && multiSelected.size > 0 ? (
                    <KbTooltip
                      tooltip='Press Shift + Enter to send'
                      tooltipProps={{ side: 'top' }}
                    >
                      <button
                        ref={pendingOptionsSendButtonRef}
                        type='button'
                        onClick={() => {
                          const prompt = pendingOptions
                          const selected = Array.from(multiSelected)
                          const selectedIndexes = selected
                            .map((label) => prompt.options.indexOf(label))
                            .filter((i) => i >= 0)
                          const text = selected.join(', ')
                          const activityContext = {
                            ...(prompt.activityContext ?? {}),
                            selectedText: text,
                          }
                          setMultiSelected(new Set())
                          submitPresentOptions({
                            prompt,
                            status: 'answered',
                            selected,
                            selectedIndexes,
                            activityContext,
                            fallbackText: text,
                          })
                        }}
                        className='inline-flex h-[26px] items-center justify-center gap-1.5 rounded-sm bg-primary px-3 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                      >
                        Send {multiSelected.size}
                        <ArrowRight className='h-3 w-3' />
                      </button>
                    </KbTooltip>
                  ) : pendingOptions.options.length === 0 &&
                    pendingOptionsOtherText.trim() ? (
                    <button
                      type='button'
                      onClick={() => {
                        const text = pendingOptionsOtherText.trim()
                        if (!text) return
                        const prompt = pendingOptions
                        const activityContext = {
                          ...(prompt.activityContext ?? {}),
                          selectedText: text,
                        }
                        setPendingOptionsOtherText('')
                        submitPresentOptions({
                          prompt,
                          status: 'answered',
                          selected: text,
                          freeform: true,
                          activityContext,
                          fallbackText: text,
                        })
                      }}
                      className='inline-flex h-[26px] items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                    >
                      Send
                      <ArrowRight className='h-3 w-3' />
                    </button>
                  ) : (
                    <button
                      type='button'
                      onClick={() => {
                        const prompt = pendingOptions
                        setMultiSelected(new Set())
                        setPendingOptionsOtherText('')
                        submitPresentOptions({
                          prompt,
                          status: 'dismissed',
                          selected: null,
                          activityContext: {
                            action: 'dismiss-options',
                            toolCallId: prompt.toolCallId,
                          },
                          fallbackText:
                            'User dismissed the question, do not proceed',
                        })
                      }}
                      className='inline-flex h-[26px] items-center justify-center rounded-md border border-border bg-card px-3 text-[11px] font-medium text-text-subtle transition-colors hover:border-border-divider hover:text-text-secondary'
                    >
                      Skip
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : !isWelcomeLayout && pendingForm ? (
            <div className='animate-in fade-in slide-in-from-bottom-2 mx-auto w-full max-w-3xl bg-card px-4 pt-2 pb-2 duration-200 ease-out'>
              <ChatInputFormCard
                spec={pendingForm.spec}
                disabled={
                  chatStatus === 'streaming' || chatStatus === 'submitted'
                }
                onSubmit={(values) =>
                  submitPresentForm({
                    prompt: pendingForm,
                    status: 'submitted',
                    values,
                  })
                }
                onCancel={() =>
                  submitPresentForm({
                    prompt: pendingForm,
                    status: 'cancelled',
                    values: {},
                  })
                }
              />
            </div>
          ) : (
            !isWelcomeLayout &&
            props.inputBanner && (
              <div className='mx-auto mb-1 w-full max-w-3xl px-4'>
                {props.inputBanner}
              </div>
            )
          )}

          {/* Click-to-continue wedge — shown whenever the sidecar (artifact
              panel) is open and the chat is idle. The sidecar pattern means
              the assistant may stop its turn while the user works in the
              panel; this gives them a one-click path to resume the
              conversation without typing. */}
          {!pendingOptions &&
            !isWelcomeLayout &&
            !props.inputBanner &&
            artifactPanelState !== null &&
            chatStatus === 'ready' &&
            messages.length > 0 &&
            messages[messages.length - 1]?.role === 'assistant' && (
              <div className='mx-auto mb-1 w-full max-w-3xl px-4'>
                <button
                  type='button'
                  onClick={() =>
                    sendMessage({
                      role: 'user',
                      parts: [{ type: 'text', text: 'Continue' }],
                    })
                  }
                  className='flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-ring/40 bg-selected/40 px-3 py-2 text-[12px] font-medium text-cobalt-strong transition-colors hover:border-ring hover:bg-selected'
                >
                  Click to continue
                  <ArrowRight className='h-3.5 w-3.5' />
                </button>
              </div>
            )}

          {/* Starter suggestions — shown above the composer in the empty
              state of a page-scoped chat so users have ideas without us
              auto-sending a question for them. Only renders when the
              current page has an entry in PAGE_SUGGESTIONS; otherwise the
              user just sees an empty composer. The welcome layout has its
              own suggestion logic in ChatWelcomeBody. */}
          {!isWelcomeLayout &&
            !pendingOptions &&
            !pendingSidecarAction &&
            messages.length === 0 &&
            chatStatus === 'ready' &&
            pageContext?.page &&
            PAGE_SUGGESTIONS[pageContext.page] && (
              <div className='mx-auto mb-1 flex w-full max-w-3xl flex-col gap-1.5 px-4'>
                {PAGE_SUGGESTIONS[pageContext.page].map((prompt) => (
                  <button
                    key={prompt}
                    type='button'
                    onClick={() =>
                      sendMessage({
                        role: 'user',
                        parts: [{ type: 'text', text: prompt }],
                      })
                    }
                    className='font-body flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-[12px] leading-[18px] font-medium text-text-body transition-colors hover:border-border-divider hover:bg-background'
                  >
                    <Sparkles className='h-3 w-3 shrink-0 text-text-placeholder' />
                    {prompt}
                  </button>
                ))}
              </div>
            )}

          {/* Input area - fixed at bottom. Hidden when a pendingOptions
              card OR a pendingSidecarAction panel is active — the panel
              replaces the PromptInput so the user has one place to answer. */}
          <div
            className={cn(
              'mx-auto w-full px-4 pt-2 pb-2',
              isWelcomeLayout ? 'max-w-[860px] bg-background' : 'max-w-3xl bg-card',
              !isWelcomeLayout &&
                (shouldShowPendingOptions || pendingSidecarAction) &&
                'hidden'
            )}
          >
            <PromptInput
              onSubmit={handleSubmit}
              globalDrop
              multiple
              onFilesAdded={handleFilesAdded}
              className={cn('border-border-divider', isWelcomeLayout ? 'rounded-lg' : 'rounded-md')}
            >
              <PromptInputBody>
                <div
                  className={`${uploadedSources.length > 0 || uploadingFiles.size > 0 || attachedDocs.length > 0 ? 'p-3 pb-0' : ''} flex w-full flex-1 flex-wrap items-center gap-2`}
                >
                  {/* RFC-068: Show uploaded files */}
                  {uploadedSources.map((source) => (
                    <UploadedAttachmentPill
                      key={source.id}
                      name={source.name}
                      contentType={source.type}
                      onRemove={() => {
                        setUploadedSources((prev) =>
                          prev.filter((s) => s.id !== source.id)
                        )
                      }}
                    />
                  ))}
                  {/* Existing vault docs / policies attached from the "+" menu.
                      Same file icon as document uploads (FileText) so the
                      composer's attachment row reads as one consistent set. */}
                  {attachedDocs.map((doc) => (
                    <AttachmentPill
                      key={`${doc.documentType}:${doc.documentId}`}
                      icon={
                        <FileText className='text-muted-foreground h-3.5 w-3.5' />
                      }
                      name={doc.title}
                      label={doc.documentType === 'policy' ? 'Policy' : 'Doc'}
                      onRemove={() =>
                        setAttachedDocs((prev) =>
                          prev.filter(
                            (d) =>
                              !(
                                d.documentType === doc.documentType &&
                                d.documentId === doc.documentId
                              )
                          )
                        )
                      }
                    />
                  ))}
                  {/* RFC-068: Show upload progress — mirrors the attached-pill
                      shape (file icon swapped for spinner) so the user can see
                      which file is uploading. */}
                  {Array.from(uploadingFiles.entries()).map(
                    ([fileId, fileInfo]) => (
                      <UploadingAttachmentPill
                        key={fileId}
                        name={fileInfo.name}
                      />
                    )
                  )}
                </div>

                <PromptInputAttachments>
                  {(attachment) => {
                    return <PromptInputAttachment data={attachment} />
                  }}
                </PromptInputAttachments>
                <PromptInputTextarea
                  ref={promptInputTextareaRef}
                  className='min-h-19 px-[14px]'
                  placeholder={
                    isWelcomeLayout || messages.length === 0
                      ? 'How can I help you today?'
                      : 'Reply...'
                  }
                  onChange={(e) => setInput(e.target.value)}
                  value={input}
                />
              </PromptInputBody>
              <PromptInputToolbar className='p-2 pt-0'>
                <PromptInputTools>
                  <PromptInputActionMenu
                    open={actionMenuOpen}
                    onOpenChange={setActionMenuOpen}
                  >
                    <PromptInputActionMenuTrigger className='size-7 !rounded-sm p-0.5'>
                      <PlusIcon className='size-4' />
                    </PromptInputActionMenuTrigger>
                    <PromptInputActionMenuContent>
                      <PromptInputActionAddAttachments />
                      <PromptInputActionMenuItem
                        onSelect={(e) => {
                          e.preventDefault()
                          setActionMenuOpen(false)
                          setAttachDocDialogOpen(true)
                        }}
                      >
                        <FilePlus2 className='size-4' /> Use existing doc or
                        policy
                      </PromptInputActionMenuItem>
                    </PromptInputActionMenuContent>
                  </PromptInputActionMenu>
                  {SHOW_CHAT_CAPTURE_SHORTCUT &&
                    (captureContext.testId || captureContext.controlDefId) &&
                    captureContext.workspaceId && (
                      <KbTooltip
                        tooltip={
                          extensionInstalled === false
                            ? 'Install Screenata Capture'
                            : 'Uses the Screenata browser extension'
                        }
                      >
                        <button
                          type='button'
                          onClick={() => handleStartBrowserCapture()}
                          disabled={!!captureStartingId}
                          className='inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-text-body transition-colors hover:border-border-divider hover:bg-background hover:text-text-secondary disabled:cursor-not-allowed disabled:opacity-60'
                          aria-label={
                            extensionInstalled === false
                              ? 'Install Screenata Capture'
                              : 'Capture Screenshots'
                          }
                        >
                          {captureStartingId === 'composer' ? (
                            <Loader2 className='size-3.5 animate-spin' />
                          ) : (
                            <Camera className='size-3.5' />
                          )}
                          <span>
                            {captureStartingId === 'composer'
                              ? 'Starting...'
                              : extensionInstalled === false
                                ? 'Install Capture'
                                : 'Capture Screenshots'}
                          </span>
                        </button>
                      </KbTooltip>
                    )}
                  {allDocContexts.length > 0 && !isGenerationMode && (
                    <div className='flex-shrink-0'>
                      <EditorContextBadge
                        contexts={allDocContexts}
                        currentDocId={docId}
                        onRemoveDoc={async (docIdToRemove) => {
                          if (!currentThreadId) return
                          const thread =
                            await ChatThreadManager.getThread(currentThreadId)
                          if (thread?.editorContext?.documents) {
                            thread.editorContext.documents =
                              thread.editorContext.documents.filter(
                                (d) => d.docId !== docIdToRemove
                              )
                            await ChatThreadManager.saveThread(thread)
                            setAllDocContexts(thread.editorContext.documents)
                          }
                        }}
                        onClearAll={async () => {
                          if (!currentThreadId) return
                          await ChatThreadManager.clearDocumentContexts(
                            currentThreadId
                          )
                          setAllDocContexts([])
                        }}
                      />
                    </div>
                  )}

                  {/* <PromptInputButton
                variant={webSearch ? 'default' : 'ghost'}
                onClick={() => setWebSearch(!webSearch)}
              >
                <GlobeIcon size={16} />
                <span>Search</span>
              </PromptInputButton> */}
                  {/* <PromptInputModelSelect
                onValueChange={(value) => {
                  setModel(value);
                }}
                value={model}
              >
                <PromptInputModelSelectTrigger>
                  <PromptInputModelSelectValue />
                </PromptInputModelSelectTrigger>
                <PromptInputModelSelectContent>
                  {models.map((model) => (
                    <PromptInputModelSelectItem key={model.value} value={model.value}>
                      {model.name}
                    </PromptInputModelSelectItem>
                  ))}
                </PromptInputModelSelectContent>
              </PromptInputModelSelect> */}
                </PromptInputTools>
                <PromptInputSubmit
                  className='size-[28px] rounded-sm'
                  disabled={
                    uploadingFiles.size > 0 ||
                    (!isStreamingResponse &&
                      !input.trim() &&
                      uploadedSources.length === 0 &&
                      attachedDocs.length === 0)
                  }
                  status={unifiedStatus}
                />
              </PromptInputToolbar>
            </PromptInput>
            <AttachDocumentDialog
              open={attachDocDialogOpen}
              onOpenChange={setAttachDocDialogOpen}
              attached={attachedDocs}
              onAttach={setAttachedDocs}
            />
            <p className='font-body mt-2 text-center text-[10px] leading-[14px] text-text-placeholder'>
              Vera is AI and can make mistakes. Please double-check responses.
            </p>
          </div>

          {isWelcomeLayout && <ChatWelcomeBody chatEventScope={chatEventScope} />}

        </div>
      )}
    </ErrorBoundary>
  )
}
